import React from 'react';
import type { FilterSEOData } from '../../types';

export const FILTERED_INVITATIONS_COPY: FilterSEOData = {
  summer: {
    description:
      "Zola's summer wedding invitation designs are a warm, fun way to invite cherished ones to your celebration. Enjoy free guest addressing and envelopes!",
    summary: (
      <>
        <p>
          Summer weddings mean sunny ceremonies, receptions late into warm nights, ice-cold
          cocktails, and natural landscapes in bloom. Capture all this and more in an equally
          beautiful wedding invitation.
        </p>
        <p>
          Zola’s collection of wedding invitation templates are sweet as summertime. Discover
          wedding designs featuring seasonal motifs like{' '}
          <a
            href="/wedding-planning/invitations/459d45ad-81b5-4546-bebf-849c42308804"
            target="_blank"
            rel="noopener noreferrer"
          >
            sunflowers
          </a>
          ,{' '}
          <a
            href="/wedding-planning/invitations/76ea90df-0a73-4534-8aa8-bb7687d543c0"
            target="_blank"
            rel="noopener noreferrer"
          >
            palm fronds
          </a>
          , and{' '}
          <a
            href="/wedding-planning/invitations/7b6c5ee7-3eec-4256-b9e9-2408f5867027"
            target="_blank"
            rel="noopener noreferrer"
          >
            lemons
          </a>
          . You might also consider scenes like a{' '}
          <a
            href="/wedding-planning/invitations/5d916696-15d4-498e-aebb-f3fef822dede"
            target="_blank"
            rel="noopener noreferrer"
          >
            mountain sunrise
          </a>{' '}
          or{' '}
          <a
            href="/wedding-planning/invitations/5328e9b8-f8ba-47e1-bcfb-5d7543e83b49"
            target="_blank"
            rel="noopener noreferrer"
          >
            sandy beach
          </a>
          .
        </p>
        <p>
          Whether you’re having a sandy wedding by the ocean or a barbeque on a ranch, Zola has a
          variety of invitation ideas and inspiration for your summer wedding.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'When should we send out our summer wedding invitations?',
        body: (
          <>
            <p>
              The typical time to mail{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              is six to eight weeks before the big day. This gives guests enough notice to RSVP,
              make arrangements and purchase{' '}
              <a href="/wedding-registry" target="_blank" rel="noopener noreferrer">
                wedding registry
              </a>{' '}
              gifts. However, there are a few exceptions to this rule. If you sent out save the
              dates, typically mailed seven to nine months in advance of the wedding, you have
              flexibility to send invitations later. If you are having a destination wedding, on the
              other hand, it is better to send your wedding invites well in advance, 12 weeks before
              the wedding day, to give guests more time to prepare.
            </p>
            <p>
              For a local summer wedding in July, for example, send out your invitations in May. For
              a destination wedding in July, send your invitations in April.
            </p>
          </>
        ),
      },
      {
        subheading: 'What other wedding stationery do we need?',
        body: (
          <>
            <p>
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                Save the dates
              </a>{' '}
              are a crucial piece of stationery for a summer wedding because many people plan summer
              vacations well in advance. Choose a save the date that matches your wedding invitation
              design and send it out at least eight to nine months before your wedding. This will
              not only ensure guests plan summer travels around your wedding, but also build
              excitement around your celebration.
            </p>
            <p>
              Additional stationery will help your wedding run even more smoothly. From day-of
              programs and menus, to post-celebration thank you cards, easily create everything you
              need at Zola.
            </p>
          </>
        ),
      },
    ],
  },
  fall_autumn: {
    summary: (
      <>
        <p>
          Fall provides a beautiful seasonal backdrop for weddings. In addition to the crisp air and
          warm hues, there is stunning scenery and incredible flavors. Don’t limit embracing the
          season to just your venue, color palette, and menu, though when wedding planning.
          Incorporate autumn into your invitation suite as well.
        </p>
        <p>
          How? Zola offers a collection of fall wedding invites that will make selecting the right
          style simple. For a fall wedding in the city, choose an understated urban option such as
          the{' '}
          <a
            href="/wedding-planning/invitations/02f539d8-8258-474b-9a47-cdd01c01abfd"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ryann invitation
          </a>
          . For a fall wedding in the country, choose a rustic floral option such as the{' '}
          <a
            href="/wedding-planning/invitations/1ac493fa-cb99-4fd7-85b7-a6b992909281"
            target="_blank"
            rel="noopener noreferrer"
          >
            Gardner Bloom invitation
          </a>
          .
        </p>
        <p>
          You can also choose an invitation with seasonal hues such as the two-toned{' '}
          <a
            href="/wedding-planning/invitations/d12227ab-2377-497b-9b0a-6613bce21903"
            target="_blank"
            rel="noopener noreferrer"
          >
            Hernando invitation
          </a>
          , which comes in rich Mahogany, Caramel, and Rust colorways or the bold{' '}
          <a
            href="/wedding-planning/invitations/8653415a-8661-4643-922b-ab46479d8519"
            target="_blank"
            rel="noopener noreferrer"
          >
            Lyons invitation
          </a>
          , which features a solid background in an assortment of fall colors.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we include on our fall wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding invitation with Zola,{' '}
              <a
                href="/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a template that is easy to customize. It will include wedding
              details regarding who’s getting married, the wedding hosts, the wedding day and
              location of the wedding ceremony, the location of the reception, if different from the
              ceremony, and how to RSVP. After you select the fall design you love, you can
              customize all of this information into your wedding invitation template.
            </p>
            <p>
              You may also decide to include an enclosure card with pertinent information your
              guests will need, including a link to your wedding website. Your wedding website is
              where you can share even more info such as the schedule of events, recommendations for
              travel and accommodations, registry information, your love story, and more. At Zola,
              your wedding website is free—and beautiful to boot! The site will match your fall
              wedding invitation to create a cohesive experience for both you and your guests.
            </p>
            <p>
              With all of these stationery pieces and tools at your disposal, the process of
              selecting, customizing, and sending your invitations is simple.
            </p>
          </>
        ),
      },
    ],
  },
  winter: {
    description:
      "Zola's winter wedding invitation designs are a sparkling, wintry way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    h1: 'All About Our Winter Wedding Invitations',
    summary: (
      <>
        <p>
          Winter weddings are full of wonder. Embrace the winter season with a color palette of icy
          blue hues or rich holiday tones for your winter wedding invitations. Keep things modern
          and classic with black and white. As another striking option, consider metallic foil in
          gold, rose gold, or silver on your winter wedding invites.
        </p>
        <p>
          For winter wedding inspiration, check out{' '}
          <a
            href="/wedding-planning/invitations/95d05708-e801-461c-812c-4db5d870a10d"
            target="_blank"
            rel="noopener noreferrer"
          >
            Belvoir
          </a>
          , featuring boughs of holly, and{' '}
          <a
            href="/wedding-planning/invitations/aea32ed7-4c78-444e-bf08-15acd8e8dfc6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Glacier
          </a>
          , available in beautiful wintery hues of blue, grey, and ivory. The gold foil-adorned{' '}
          <a
            href="/wedding-planning/invitations/7ad52837-e2fd-4f8d-ab65-298a1b4963d0"
            target="_blank"
            rel="noopener noreferrer"
          >
            Baker
          </a>{' '}
          evokes New Year’s Eve, while{' '}
          <a
            href="/wedding-planning/invitations/7f2f23df-7fe9-4496-b07e-b0a92fde4ec4"
            target="_blank"
            rel="noopener noreferrer"
          >
            Moonlight
          </a>{' '}
          features fairy lights in subtle nod to winter weddings. Zola has wedding invitations for
          every style of winter wedding.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'When should we send out our winter wedding invitations?',
        body: (
          <>
            <p>
              Consider mailing your{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              six to eight weeks before your wedding date. However, there’s nothing wrong with
              giving guests even more time to plan for your wedding—and yourselves more time to
              collect RSVPs and make table arrangements. If you have capacity limits, it is an
              especially good idea to send out invitations early, allowing you to watch closely and
              invite guests off your maybe list.
            </p>
            <p>
              For a winter wedding, send invitations between late October and January. By sending
              invitations in late November or after the new year, you avoid the rush of holiday
              cards and busy post offices. If you are having a holiday wedding between Christmas and
              New Year’s Eve, consider sending your invitations more than eight weeks in advance,
              giving guests ample time to make arrangements for your wedding.
            </p>
          </>
        ),
      },
      {
        subheading: 'What other wedding stationery do we need?',
        body: (
          <>
            <p>
              Wedding stationery isn’t limited to wedding invitations. You may also need enclosure
              cards for details and RSVPs, menus, programs,{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , and more. Don’t forget thank you cards, too! Find everything you need to create your
              coordinating stationery suite at Zola. You can even create a matching wedding website.
              With all your wants in one place, Zola makes it easy to check stationery off your{' '}
              <a
                href="/expert-advice/checklist/your-ultimate-wedding-planning-checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding planning checklist
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
  spring: {
    description:
      "Zola's spring wedding invitation designs are a cheerful, lively way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          Spring signifies new beginnings and the start of wedding season. This time of year is full
          of blooming flowers in nature and on wedding invitations. Take advantage of stunning
          wedding invitation designs, like{' '}
          <a
            href="/wedding-planning/invitations/22b13c6e-1a13-4a78-96c8-f5d9d5cde78c"
            target="_blank"
            rel="noopener noreferrer"
          >
            Octavia
          </a>{' '}
          or{' '}
          <a
            href="/wedding-planning/invitations/9d933b5b-fd4d-4ec5-955c-9596af542a8a"
            target="_blank"
            rel="noopener noreferrer"
          >
            Destin
          </a>
          . If your spring wedding theme is more subdued, opt for a clean wedding invitation design
          template in a pale shade, like{' '}
          <a
            href="/wedding-planning/invitations/0549bbc4-d3e1-4620-9789-3b45b01d2ac1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Muriano
          </a>
          .
        </p>
        <p>
          When customizing your spring wedding invitations, choose a color palette that speaks to
          the season. If you’re having a garden celebration, consider pastel pinks, greens, and
          peaches. If your wedding theme is more modern, don’t shy away from bright hues. We
          recommend{' '}
          <a
            href="/wedding-planning/invitations/c767c9b6-ae3f-42e6-a25c-221d6bd98e0d"
            target="_blank"
            rel="noopener noreferrer"
          >
            Bow Bridge
          </a>{' '}
          in sky blue, or{' '}
          <a
            href="/wedding-planning/invitations/e306e150-d02e-4bcb-a787-9af8d01b6b8a"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stowe
          </a>{' '}
          in coral.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What other wedding stationery do we need?',
        body: (
          <>
            <p>
              While the wedding invite is the star of the show, there are other ways to incorporate
              your chosen invitation design into your wedding stationery. Select coordinating
              enclosure cards,{' '}
              <a href="/wedding-planning/programs/shop" target="_blank" rel="noopener noreferrer">
                programs
              </a>
              , menus, and website templates to help your spring wedding bloom to life.
            </p>
          </>
        ),
      },
      {
        subheading: 'When should we send out our spring wedding invitations?',
        body: (
          <>
            <p>
              It’s customary to send out{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              6-8 weeks before your wedding date. This will give guests enough time to RSVP and you
              the opportunity to send out additional invitations should you receive declines.
            </p>
            <p>For a spring wedding, you will likely send out invitations in February or April.</p>
            <p>
              Keep in mind, spring is a popular season for weddings. Many occasions could conflict
              with your wedding date, like Easter, spring breaks, Mother’s Day, Father’s Day,
              graduations, and Memorial Day. If you are planning a spring wedding, let your guests
              know your wedding date well in advance by sending a{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the date
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
  elegant_formal: {
    faqContent: [
      {
        subheading: 'What should we include on our elegant wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding invitation with Zola,{' '}
              <a
                href="https://www.zola.com/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a design template that is easy to customize. When you select a
              style from our collection of elegant luxury wedding invitations, it will include who’s
              getting married, the wedding hosts (usually the bride’s and/or groom’s parents), when
              and where the wedding is, the wedding’s tone and formality, and how to RSVP.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we word formal elegant wedding invitations?',
        body: (
          <>
            <p>
              There are so many ways to word your invitations. Thankfully, Zola makes it easy to
              settle on the right wording with plenty of wedding invitation templates to choose from
              and customize. When you browse the selection of fancy wedding invitations, you’ll find
              proper wording for a formal wedding, as well as less formal options that are just as
              timeless, so you can set just the right tone. To make sure you hit all the right
              notes, check out{' '}
              <a
                href="https://www.zola.com/expert-advice/the-dos-and-donts-of-wedding-invitation-wording"
                target="_blank"
                rel="noopener noreferrer"
              >
                the Do’s and Don’ts of Wedding Invitation Wording
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we select the right invitation for us?',
        body: (
          <>
            <p>
              There are so many beautiful elegant wedding invitations to choose from, which can make
              selecting the right one a bit overwhelming. In addition to searching Zola’s selection
              of invitations by style, you can also search by color and orientation (landscape or
              portrait), and narrow down the results even further by selecting metallic foil
              lettering or letterpress printing if you so choose, both of which add an elegant flair
              to any card stock product.
            </p>
          </>
        ),
      },
      {
        subheading: 'What makes up the formal invitation suite?',
        body: (
          <>
            <p>
              While the wedding invite is the star of the show, there are other paper pieces you may
              choose to send with the invitation. For formal elegant wedding invitations, in
              particular, you will also want to include an RSVP card and a details card. These
              cards, along with the formal invitation, will provide guests with all the information
              they will need to celebrate with you on your wedding day. Once you choose the Zola
              wedding invitation design that’s right for you, selecting the accompanying parts is
              easy; there is a matching suite for every style.
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              In addition to the pieces that make up your invitation suite, you may also want to
              select wedding day stationery from Zola that matches your elegant wedding invitations.
              From programs to menus and even thank you cards, you can create everything at Zola.
            </p>
          </>
        ),
      },
    ],
  },
  classic_traditional: {
    summary: (
      <>
        <p>
          If you’re looking for classic wedding invitation ideas, you will have plenty of options to
          choose from. Just because they err on the side of tradition doesn’t mean these classic
          wedding invitations are any less exciting. In addition to searching Zola’s selection of
          invitations by style, you can also search by color and orientation (portrait is ideal for
          classic formal wedding invitations), and narrow down the results even further by selecting
          foil lettering or letterpress (letterpress being the more classic choice).
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we include on traditional wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding day invitation with Zola,{' '}
              <a
                href="https://www.zola.com/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a template that is easy to customize. Traditional or classic
              wedding invitations should include the names of those getting married, the names of
              the wedding hosts (usually the bride and/or groom’s parents), when and where the
              wedding will be, the wedding’s formality or dress code, and how to RSVP for the big
              day. You may also choose to include information such as dress code and RSVP on a
              response card and details card sent along with the formal wedding invitation.
              Including these additional cards is a hallmark of traditional wedding invitations.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we incorporate traditional wedding wording?  ',
        body: (
          <>
            <p>
              Formal invitation wording that follows proper etiquette can be tricky business but
              Zola makes it easy with plenty of wedding invitation templates to choose from that
              have all the right words already in place and are easy to customize. When you browse
              the selection of classic wedding invitations, you’ll find proper wording for a
              traditional ceremony, as well as less formal options so you can set just the right
              tone. To make sure you hit all the right notes, check out{' '}
              <a
                href="https://www.zola.com/expert-advice/the-dos-and-donts-of-wedding-invitation-wording"
                target="_blank"
                rel="noopener noreferrer"
              >
                the Do’s and Don’ts of Wedding Invitation Wording
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
  floral: {
    description:
      "Zola's floral wedding invitation designs are a vibrant, stylish way to invite cherished ones to your celebration. Get free guest addressing and more!",
    summary: (
      <>
        <p>
          There are countless unique floral themed wedding invitations to suit every{' '}
          <a
            href="https://www.zola.com/expert-advice/whats-your-wedding-style"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding style
          </a>
          . Consider your venue, wedding theme, and personal style before getting lost in all of the
          beautiful options. Then, narrow down your search by selecting a color palette, foil
          lettering, letterpress, orientation, and whether or not you want a featured photo, all of
          which you can do when you search Zola’s collection of botanical and floral wedding
          invitation templates.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'When should we choose floral wedding invitations?',
        body: (
          <>
            <p>
              Floral wedding invitations come in a variety of styles that can complement a variety
              of wedding settings. They can be soft and romantic, perfect for a garden wedding; bold
              and moody, perfect for an intimate winter affair; or bright and whimsical, perfect for
              a spring or summer reception. If the idea of a flower wedding invitation speaks to
              you, there is surely a floral design that will suit your wedding, regardless of the
              setting and formality.
            </p>
          </>
        ),
      },
      {
        subheading: 'Can we customize our invitation?',
        body: (
          <>
            <p>
              Yes! An invitation sets the tone for the wedding, so it should be an expression of
              your personality and wedding theme, and therefore customizable. With Zola, you can{' '}
              <a
                href="https://www.zola.com/expert-advice/unique-wedding-invite-ideas-design-delivery-and-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                put your own unique spin
              </a>{' '}
              on the floral wedding invitation template of your choice. When creating custom wedding
              invitations, you have eight different paper options, countless font styles, luxurious
              foil accents, and more to make your invitations all your own. With all of these
              customizations, your invitation can reflect your special day, your way.
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              Once you have selected and customized the perfect floral wedding invitation, you may
              decide that you want to use the same floral design you fell in love with elsewhere.
              With Zola, you can select wedding day stationery that matches your custom wedding
              invitations. From programs to menus and even thank you cards, you can create
              everything at Zola.
            </p>
          </>
        ),
      },
    ],
  },
  greenery: {
    description:
      "Zola's greenery wedding invitation designs are a bright, leafy way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          There are a variety of styles of greenery wedding invitations you may want to consider. If
          you are drawn to romantic, minimalist styles, eucalyptus wedding invitations such as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/5d17b42c-508f-480e-948d-30689b9cf460"
            target="_blank"
            rel="noopener noreferrer"
          >
            Galata invitation
          </a>{' '}
          will frame the details of the big day beautifully and feel just right. If you are having a
          desert wedding or have a bohemian style, you may like something with a palm frond or
          cactus such as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/d9ed95e5-9eba-4266-aa0c-d613eeb6eef6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Escape invitation
          </a>
          .
        </p>
        <p>
          Botanical wedding invitations can feature greenery, or florals, or both. Some botanical
          designs are subtle while others are more pronounced. For a spring celebration, look for
          something fresh and pastel such as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/d766d3e4-4264-4e79-8a09-74c960a00cf2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Crofton invitation
          </a>
          , which features pretty tulips and modern typography . Subtle motifs can also add just the
          right botanical touch to your invitations like the ones seen on our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/fd6c6e22-b255-4769-b6e2-ba96faa2d7b1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Creston
          </a>{' '}
          and{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/21feb808-1f10-4c74-984a-cdccaab4d7c5"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cardinell
          </a>{' '}
          invitation designs.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Can we customize our invite?',
        body: (
          <>
            <p>
              Yes! An invitation sets the tone for the wedding, so it should be an expression of
              your personality and wedding theme, and therefore customizable. With Zola, you can put{' '}
              <a
                href="https://www.zola.com/expert-advice/unique-wedding-invite-ideas-design-delivery-and-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                your own unique spin
              </a>{' '}
              on the wedding invitation template of your choice. You have eight different paper
              options, countless font styles, luxurious foil accents, and more to make your wedding
              invitations all your own. With all of these customizations, your invitation can
              reflect your special day, your way.
            </p>
          </>
        ),
      },
      {
        subheading: 'What makes up the invitation suite?',
        body: (
          <>
            <p>
              While the invitation is the star of the show, there are other paper pieces you may
              choose to send with the invitation such as a response card and a details card. These
              cards, along with the invite, will provide guests with all the information they will
              need to celebrate with you on your wedding day. Once you choose the Zola design that’s
              right for you, selecting the accompanying parts is easy; there is a matching suite for
              every style so the same greenery or botanical theme you choose for your wedding
              invitations can be on your details cards and response cards as well.
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              Once you have selected and customized the perfect wedding invitation suite, you may
              decide that you want to use the same design elsewhere. With Zola, you can select
              wedding stationery that matches your botanical or greenery wedding invitations
              including programs, menus, and thank you cards.
            </p>
          </>
        ),
      },
    ],
  },
  rustic: {
    description:
      "Zola's rustic and country wedding invitation designs are the perfect way to invite cherished ones to your celebration. Enjoy free envelopes!",
    summary: (
      <>
        <p>
          “Rustic” and “country” are sometimes used interchangeably but can mean very different
          things when you’re talking about the wedding theme. A rustic themed wedding invitation is
          one that features natural, soft, or romantic elements, such as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/c4cd6264-ffdc-44ef-8551-ae7fb24f0ace"
            target="_blank"
            rel="noopener noreferrer"
          >
            Rhys invitation
          </a>
          .
        </p>
        <p>
          Country wedding invitations often feature natural elements as well, but also denote a more
          casual tone and outdoorsy setting. For your country wedding theme, opt for a design such
          as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/459d45ad-81b5-4546-bebf-849c42308804"
            target="_blank"
            rel="noopener noreferrer"
          >
            Bonnie invitation
          </a>
          , which features hand painted sunflowers, our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/5074f429-4c9b-47a1-ae96-6acc23d4dfb5"
            target="_blank"
            rel="noopener noreferrer"
          >
            Camden invitation
          </a>
          , which features watercolor pine trees, or our charming{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/735a5234-edfb-4ca3-bad6-993a8cbc5fee"
            target="_blank"
            rel="noopener noreferrer"
          >
            Festival invitation
          </a>
          .
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we include on our rustic themed wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding day invitation with Zola,{' '}
              <a
                href="https://www.zola.com/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a template that is easy to customize. When you select a style
              from our collection of rustic country wedding invitations, it will include who’s
              getting married, the wedding hosts (usually the bride and/or groom’s parents), when
              and where the wedding venue is, the wedding’s formality, and how to RSVP. You can
              choose to leave all this information as is or alter the invitation design.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we word rustic wedding invitations?',
        body: (
          <>
            <p>
              There are so many ways to word your invitations. Thankfully, Zola makes it easy to
              settle on the right wording with plenty of wedding invitation templates to choose from
              and customize. When you browse the selection of rustic wedding invitations, you’ll
              find a variety of options ranging in formality so you can set just the right tone. If
              you’re having a casual country celebration, you may want to choose a less
              formally-worded template, and if you’re having a more refined rustic wedding, you may
              want to choose a more formally-worded template. To make sure you hit all the right
              notes, check out{' '}
              <a
                href="https://www.zola.com/expert-advice/the-dos-and-donts-of-wedding-invitation-wording"
                target="_blank"
                rel="noopener noreferrer"
              >
                the Do’s and Don’ts of Wedding Invitation Wording
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
  destination: {
    description:
      "Zola's destination wedding invitation designs are a travel-themed way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          When selecting your wedding invitations, let the setting inspire you! There are plenty of
          destination themed wedding invitations and travel themed wedding invitations to choose
          from including tropical designs suitable for tropical destinations such as our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/98c439d8-72bf-4afd-92cf-c85b80f9ad03"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cartago invite
          </a>
          ,{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/fe1137d5-19b9-42d3-9462-551325cca079"
            target="_blank"
            rel="noopener noreferrer"
          >
            skyline illustrations
          </a>{' '}
          that are fitting for a variety of cities, and{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/ec57f548-67b8-48ff-8999-43c489de41de"
            target="_blank"
            rel="noopener noreferrer"
          >
            landscape scenes
          </a>{' '}
          perfect for a mountain wedding.{' '}
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we include on our destination wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding invitation with Zola,{' '}
              <a
                href="https://www.zola.com/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a template that is easy to customize. However, when you’re
              having a destination wedding, there are a few additional details that are important to
              include with the invitation, if not on it. Think things like: lodging information,
              directions, and transportation in addition to the basics, from who’s getting married
              to the wedding hosts, when and where the wedding is, the dress code, and how to RSVP.{' '}
            </p>
            <p>
              It’s also good etiquette to invite all out of town wedding guests (this means everyone
              for a destination wedding) to the rehearsal dinner or welcome party, so you need to
              make guests aware of this event as well. If you don’t want to include all of this
              information on a details card or the actual invitation, you can also direct guests to
              your wedding website for more information. To ensure you don’t miss a thing, keep this{' '}
              <a
                href="https://www.zola.com/expert-advice/destination-wedding-checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                ultimate destination wedding checklist
              </a>{' '}
              handy.
            </p>
          </>
        ),
      },
      {
        subheading: 'When should we send out our destination wedding invitations?',
        body: (
          <>
            <p>
              Typically, the time to send out your wedding invites is 6-8 weeks before the ceremony.
              However, with a destination wedding, it’s advised to send out your invites 12 weeks
              before the wedding date. This gives your guests an ample amount of time to RSVP and
              gives you time to receive an accurate guest count for the reception.
            </p>
            <p>
              For destination weddings, sending{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              is also critical. Since your guests will be traveling for your wedding, the more time
              you give them to plan, the better, so try to send these out 9 months before the big
              day, or ASAP. If your timeline is tight, don’t worry. Most of Zola’s invites and save
              the dates can be sent to you within 5-10 business days, so you can quickly receive
              your custom order and mail your destination wedding invitations to guests shortly
              after.
            </p>
          </>
        ),
      },
    ],
  },
  beach: {
    description:
      "Zola's beach wedding invitation designs are a scenic, tropical way to invite cherished ones to your celebration. Shop now with free addressing!",
    summary: (
      <>
        <p>
          Whether you’re saying “I do” on the sand of your favorite beach, or you’re traveling to a
          tropical destination for your nuptials, set the tone for your guests from the very
          beginning by selecting beach themed wedding invitations. Beach wedding invitations come in
          a variety of styles and colors; you can go all out with beachy motifs as seen on our{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/b4993c4f-2326-4411-b8d7-659e7879afea"
            target="_blank"
            rel="noopener noreferrer"
          >
            Wrightsville invitation
          </a>
          , opt for a nautical style with a design such as{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/8ed00cd0-8a9f-48c9-ba65-ba1407ff6ee6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Potter
          </a>
          , or keep the design simple and choose a color palette that follows a beach theme. With
          plenty of tropical wedding invitations and ocean-themed wedding invitations to choose
          from, too, the options for your sandy celebration are endless.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we include on our beach wedding invitations?',
        body: (
          <>
            <p>
              When crafting your wedding day invitation with Zola,{' '}
              <a
                href="https://www.zola.com/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                all the information you need to include
              </a>{' '}
              is provided for you in a template that is easy to customize including who’s getting
              married, the wedding hosts, when and where the wedding ceremony is, and how to RSVP.
              However, when you’re having a{' '}
              <a
                href="https://www.zola.com/inspiration/real-weddings/beaches-waterfronts"
                target="_blank"
                rel="noopener noreferrer"
              >
                beach wedding
              </a>
              , you may need to include more specific directions to the location and instructions on
              attire.
            </p>
            <p>
              If you don’t want to include all of this information on the actual invitation, you can
              send your beach wedding invitations with RSVP cards and details cards or direct guests
              to your wedding website for more information on the style.
            </p>
          </>
        ),
      },
      {
        subheading: 'Can we customize our invitation?',
        body: (
          <>
            <p>
              Yes! An invitation sets the tone for the wedding reception, so it should be an
              expression of your personality and wedding theme, and therefore customizable. With
              Zola, you can put{' '}
              <a
                href="https://www.zola.com/expert-advice/unique-wedding-invite-ideas-design-delivery-and-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                your own unique spin
              </a>{' '}
              on the wedding invitation template of your choice. You have eight different paper
              options, countless font styles, luxurious foil accents, and more to make your beach
              theme wedding invitations all your own. With all of these customizations, your
              invitation can reflect your special day, your way.{' '}
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              Once you have selected and customized the perfect wedding invitation, you may decide
              that you want to use the beachy design for all of your stationery. With Zola, you can
              select wedding day stationery that matches your wedding invitations including
              programs, menus, and even thank you cards.
            </p>
          </>
        ),
      },
    ],
  },
  boho: {
    description:
      "Zola's boho wedding invitation designs are an earthy, romantic way to invite cherished ones to your celebration. Get free guest addressing, and more!",
    summary: (
      <>
        <p>
          An invitation sets the tone for the wedding day, so it should be an expression of your
          personality and wedding theme. If your{' '}
          <a
            href="https://www.zola.com/expert-advice/rustic-aisle-runner-decoration-for-a-boho-wedding"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding is bohemian
          </a>
          , your style of invitations should be, too. After all, this is the first piece of your
          wedding your guests will see. Our collection of boho wedding invitations feature earthy
          details and romantic charm that sets them apart from traditional wedding invitations.
          Choose from an array of unique options to find the design that speaks to you. The{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/54f936ee-3aa7-4449-a793-2f6a14622330"
            target="_blank"
            rel="noopener noreferrer"
          >
            Aurora invitation
          </a>{' '}
          is perfect for the boho fete in the desert, while the{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/c074efc5-b21d-47e8-971e-78f1f4360442"
            target="_blank"
            rel="noopener noreferrer"
          >
            Tamarine invitation
          </a>{' '}
          is better suited for a wedding with a worldly flair. Is your bohemian style more vintage
          in nature? Try our{' '}
          <a
            href="https://qa.zola.com/wedding-planning/invitations/348f7b58-16d4-4868-8b61-43f121724aaf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Kianna invitation
          </a>{' '}
          on for size. You can’t go wrong with any of these boho wedding invitations to give guests
          a sneak peek into your big day.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Can we customize our invitation?',
        body: (
          <>
            <p>
              Yes! We know customization is important for every couple, so at Zola, we made sure you
              can put{' '}
              <a
                href="https://www.zola.com/expert-advice/unique-wedding-invite-ideas-design-delivery-and-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                your own unique spin
              </a>{' '}
              on the boho wedding invitation template you choose. You have eight different paper
              options, countless font styles, luxurious foil accents, and more to make your wedding
              invitations totally your own. With all of these customizations, your invitation can
              reflect your special day, your way.
            </p>
          </>
        ),
      },
      {
        subheading: 'Are there affordable boho wedding invitations available?',
        body: (
          <>
            <p>
              At Zola, all of our wedding invitations, even those with unique bohemian designs, are
              incredibly affordable. All invites come with free free guest addressing, free
              envelopes, and, if your date ever changes, free change the dates. So, creating your
              custom invitations with Zola will not only save you money, but also time.
            </p>
            <p>
              Plus, in addition to offering affordable invitations, Zola has all of your other
              wedding stationery needs at affordable prices. You can select enclosure cards, thank
              you notes, programs, and menus—all to match your boho wedding invitation. This way,
              your stationery can be seamless and the process of selecting it can be, too.
            </p>
          </>
        ),
      },
    ],
  },
  simple_minimalist: {
    summary: (
      <>
        <p>
          We offer lots simple, yet stylish, wedding invitations to suit your minimalist style! You
          can search by color and orientation (landscape or portrait), and narrow down the results
          even further by selecting foil lettering or letterpress printing if you so choose.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we word simple wedding invitations?',
        body: (
          <>
            <p>
              There are so many ways to word your invitations, but if you’re going for a minimalist
              style, the fewer words the better. Zola makes it easy to settle on the right wording
              with plenty of wedding invitation templates to choose from and customize. When you
              browse the selection of minimalist wedding invitations, you’ll see a range of options
              that are short and to the point, but still elegant and informational. To make sure you
              hit all the right notes, check out{' '}
              <a
                href="https://www.zola.com/expert-advice/the-dos-and-donts-of-wedding-invitation-wording"
                target="_blank"
                rel="noopener noreferrer"
              >
                the Do’s and Don’ts of Wedding Invitation Wording
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'What makes up the invitation suite?',
        body: (
          <>
            <p>
              When taking a minimalist approach to your wedding invitation design, you may choose to
              include more wordy information on{' '}
              <a
                href="https://www.zola.com/expert-advice/make-wedding-website"
                target="_blank"
                rel="noopener noreferrer"
              >
                your wedding website
              </a>{' '}
              or on enclosure cards such as a response card and a details card. These cards, along
              with the custom invitation, will provide guests with all the information they will
              need to RSVP and celebrate with you on your wedding day. Once you choose the Zola
              design that’s right for you, selecting the accompanying parts is easy; there is a
              matching suite for every style.{' '}
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              In addition to the pieces that make up your invitation suite, you may also want to
              select wedding day stationery from Zola that matches your simple wedding invitations.
              From programs to menus and even thank you cards, you can create everything at Zola so
              all of your stationery will appear streamlined and sophisticated.
            </p>
          </>
        ),
      },
    ],
  },
  modern: {
    description:
      "Zola's modern and contemporary wedding invitation designs are the perfect way to invite cherished ones to your celebration. Enjoy free envelopes!",
    summary: (
      <>
        <p>
          It’s important to note that “modern” can mean many things; it is whatever modern style
          means to you. In general though, modern unique wedding invitations push the boundaries of
          tradition or do away with them entirely. They often feature either sleek minimalistic
          fonts or loose modern calligraphy, cool geometric designs or fluid abstract ones, and
          often incorporate metallic hues or shades from a bold color palette. If you’re having a{' '}
          <a
            href="https://www.zola.com/expert-advice/how-to-pull-off-a-modern-wedding-theme"
            target="_blank"
            rel="noopener noreferrer"
          >
            modern wedding
          </a>
          , don’t be afraid to select an invitation that is a bit outside the box. Consider
          geometric patterns as seen on the{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/da20d174-1bad-45ed-b76b-03900b84b230"
            target="_blank"
            rel="noopener noreferrer"
          >
            Davies invitation
          </a>
          , and bold typography as seen on the{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/d7ca6848-fd11-43fa-ae1d-2d882bc037af"
            target="_blank"
            rel="noopener noreferrer"
          >
            Carlisle invitation
          </a>
          .
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we select the right invitation for us?',
        body: (
          <>
            <p>
              There are so many beautiful modern wedding invitations to choose from, which can make
              selecting the right one a bit overwhelming. In addition to searching Zola’s selection
              of invitations by style, you can also search by color and orientation (landscape or
              portrait), and narrow down the results even further by selecting foil lettering or
              letterpress if you so choose. For contemporary wedding invitations, consider unique
              prints and fonts, foil lettering, and color. All of these elements will give your
              invitation the modern twist you’re looking for.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we word modern wedding invitations?',
        body: (
          <>
            <p>
              Wording your wedding invitations can be tricky business. Thankfully, Zola makes it
              easy to settle on the right wording with plenty of wedding invitation design templates
              to choose from and customize. When you browse the selection of modern wedding
              invitations, you’ll find an array of options ranging from formal to casual so you can
              set just the right tone. To make sure you hit all the right notes and follow common
              etiquette, check out{' '}
              <a
                href="https://www.zola.com/expert-advice/the-dos-and-donts-of-wedding-invitation-wording"
                target="_blank"
                rel="noopener noreferrer"
              >
                the Do’s and Don’ts of Wedding Invitation Wording
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'What makes up the invitation suite?',
        body: (
          <>
            <p>
              For modern and contemporary wedding invitations, in particular, you may prefer to
              direct guests to your{' '}
              <a
                href="https://www.zola.com/expert-advice/make-wedding-website"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding website
              </a>{' '}
              to RSVP and get more information. However, you can also include an RSVP card and a
              details card with the invitation to complete the invitation suite if you so choose.
              Your wedding website or these additional cards will provide guests with all the
              information they will need to celebrate with you on your wedding day. Once you choose
              the Zola invitation design that’s right for you, selecting the accompanying parts is
              easy; there are matching cards and wedding websites for every style.
            </p>
          </>
        ),
      },
    ],
  },
  vintage_retro: {
    summary: (
      <>
        <p>
          Vintage-themed wedding invitations suit weddings with a vintage feel, but there are other
          circumstances where they might be a good fit, too. Vintage wedding invites can feature
          romantic and floral details that make them equally fitting for rustic or garden weddings.
        </p>
        <p>
          Zola has vintage wedding invitations to match every style and theme. Opt for{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/e0e62da9-8d00-4a61-9e89-75b91edf8095"
            target="_blank"
            rel="noopener noreferrer"
          >
            Augusta
          </a>{' '}
          for art deco flair,{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/0d2fd7c3-ba19-4612-8843-09b9dcb5e606"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cabrillo
          </a>{' '}
          for a whimsical, floral feel, or{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/2ed3d7b2-7336-4f0a-8c2c-7d7a7872c5fc"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ivy
          </a>{' '}
          for a romantic, countryside mood.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we select the right invitations for us?',
        body: (
          <>
            <p>
              While your wedding invitations are one of the first things guests see, they come after
              much of the vision for your wedding has already been decided. Good news for you! It
              makes selecting the right invitations much easier. Your invitations should reflect the
              wedding theme, color palette, and feel.
            </p>
            <p>
              You can even filter your search for the perfect invitation on Zola. Select style,
              color, photo, foil and letterpress options based on your personal preferences and
              wedding theme to narrow down your hunt.
            </p>
          </>
        ),
      },
      {
        subheading: 'What additional wedding stationery do we need?',
        body: (
          <>
            <p>
              In addition to your{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              and accompanying parts, like response cards, envelopes, and detail inserts, you will
              also need day-of wedding stationery, like menus, programs, and place cards. You might
              also want{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              and{' '}
              <a
                href="https://www.zola.com/wedding-planning/thank-you-cards/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                thank you cards
              </a>
              .
            </p>
            <p>
              Zola has stationery that is cohesive from start to finish. Every vintage wedding
              invitation design has matching day-of stationery, save the dates, and thank you cards.
              Zola makes your stationery not only cohesive, but also easy to select. Less to worry
              about!
            </p>
          </>
        ),
      },
    ],
  },
  watercolor: {
    description:
      "Zola's watercolor wedding invitation designs are a beautiful, artistic way to invite cherished ones to your celebration. Enjoy free addressing!",
    summary: (
      <>
        <p>
          Watercolor wedding invites are perfect for a whimsical, artistic, seaside, or destination
          wedding. They carry an air of relaxation, creativity, and fun, ideal for any of these
          wedding settings. You can find a variety of watercolor invites on Zola that fit your
          celebration and style. Go for a wavy pattern with{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/3766ed97-6699-4c32-9aad-44e5a982800a"
            target="_blank"
            rel="noopener noreferrer"
          >
            Waterfront
          </a>
          , a destination-specific look with{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/ec57f548-67b8-48ff-8999-43c489de41de"
            target="_blank"
            rel="noopener noreferrer"
          >
            Deepcreek
          </a>
          , or a modern, artful aesthetic with{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/b58e4f9d-62ab-4bfb-82c2-9c7713e42ae6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Bedford
          </a>
          .
        </p>
        <p>
          Most watercolor wedding invitations use light pastel hues and soft brushstrokes, giving
          them a romantic feel. You can complement their whimsy with loopy calligraphy, or contrast
          it with a clean, modern typeface. The choice is up to you.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we customize our watercolor wedding invitation?',
        body: (
          <>
            <p>
              All of our wedding invitation templates are customizable, allowing you to put{' '}
              <a
                href="https://www.zola.com/expert-advice/unique-wedding-invite-ideas-design-delivery-and-more"
                target="_blank"
                rel="noopener noreferrer"
              >
                your own personal spin
              </a>{' '}
              on the watercolor invitations of your choice. Play with different paper options,
              fonts, foil accents, and more to make your invitations your own.
            </p>
            <p>
              Start your wedding planning by selecting the{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitation
              </a>{' '}
              template that speaks to you. Then, choose a color that matches your wedding palette.
              You can also select foil lettering and paper options. Next, personalize the template
              with wedding details like names, date, and location. You can alter the wording to suit
              your personality and wedding formality, too.
            </p>
            <p>
              Once you make your design choices, select the number of invitations you need or order
              a free sample to ensure everything in your invitation appears just as you wish.
            </p>
            <p>
              Are you a graphic designer or artist yourself? You can even design your own wedding
              invitation template and upload it to Zola! Take advantage of our helpful features like
              free guest addressing, envelopes, and shipping. With many customization options, your
              invitations can reflect your wedding day, your way.
            </p>
          </>
        ),
      },
    ],
  },
  black: {
    description:
      "Zola's black wedding invitation designs are an elegant, bold way to invite cherished ones to your celebration. Plus get free envelopes, and more!",
    summary: (
      <>
        <p>
          Timeless and versatile, black wedding invitations allow for your celebration’s unique
          flair to shine through. They’re commanding but not overpowering, much like an iconic
          little black dress or a refined tuxedo.
        </p>
        <p>
          You can dress up your black wedding invitations with elegant gold foil, or give them a
          twist with surprising pops of color—the choice is yours.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with black wedding invitations?',
        body: (
          <>
            <p>
              Every couple is unique, and so is every wedding. Because of their versatility, you can
              adapt your black wedding invitations to your own wedding vibe. Here are just a few
              ideas when planning for the big day:
            </p>
            <p>
              <strong>Vintage</strong> – While color trends may come and go, black never goes out of
              style. If your wedding day is inspired by the styles of yesteryear, black vintage
              wedding invitations are the way to go. Reminiscent of black and white movies, film
              photography, and glamorous magazine covers, the color calls upon vintage inspiration
              and today’s modern styles.
            </p>
            <p>
              <strong>Classic & Formal</strong> – There’s a reason an elegant, formal party is
              called a “black-tie event.” In fashion, black is sleek, luxe, and beautiful. The same
              extends to wedding invitations. Black wedding invitations are a wonderful fit for a
              dressy celebration.
            </p>
            <p>
              <strong>Winter</strong> – There’s something so dreamy about winter, and a wonderland
              wedding captures the magic of the season. Black wedding invitations invoke winter’s
              long nights and cool temperatures, reminding us of the warmth and love we find in
              their midst.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect black wedding invitations for our big day?',
        body: (
          <>
            <p>
              You can take black wedding invitations in many directions—this should be exciting, not
              overwhelming! Zola’s wedding invitation design studio offers many ways for you to
              create the custom wedding invitation of your dreams, such as:
            </p>
            <p>
              <strong>Custom Photo</strong> – An elegant black-and-white photo of you and your
              partner is a beautiful portrayal of the love you share. Black background wedding
              invitations will help highlight this image and share it with all the meaningful people
              in your life.
            </p>
            <p>
              <strong>Foil</strong> – Like stars against a dark night sky, metallic foil accents can
              make black wedding invitations sparkle. This simple touch will take your
              invitations—and your celebration—to the next level.
            </p>
            <p>
              <strong>Design</strong> – Whether you want something low-key or dressed-up, subdued or
              extravagant, you can customize the design of your black wedding invitations to your
              heart’s content by playing with typography and accent colors.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our dream black wedding invitations?',
        body: (
          <>
            <p>
              Planning any big event can feel overwhelming, but it doesn’t have to be. Zola is here
              to streamline and simplify your{' '}
              <a href="/wedding-planning" target="_blank" rel="noopener noreferrer">
                wedding planning
              </a>{' '}
              process by helping you create the perfect black{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>
              . Not sure about your design? Zola offers five free design samples before you order,
              as well as other perks such as free envelopes, guest addressing, change the dates, and
              shipping.
            </p>
            <p>You can’t go wrong with Zola’s black wedding invitations.</p>
          </>
        ),
      },
    ],
  },
  blue: {
    description:
      "Zola's blue wedding invitation designs are a timeless, modern way to invite cherished ones to your celebration. Enjoy free addressing and envelopes!",
    summary: (
      <>
        <p>
          A primary color as expansive as the ocean or the sky, blue is a stunning reminder of the
          beauty of the natural world and a classic choice for a wedding invitation. Blue{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          are multifaceted and flexible, making them an easy choice for a variety of{' '}
          <a
            href="/wedding-vendors/search/wedding-venues"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding venues
          </a>{' '}
          and styles.
        </p>
        <p>
          From breezy, casual light blue wedding invitations to highly sophisticated Tiffany blue
          wedding invitations, there are plenty of hues and tones to express just what your wedding
          day means to you and your partner.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with blue wedding invitations?',
        body: (
          <>
            <p>
              Blue invites are timeless and classic, but a few themes pair particularly well with
              this versatile hue:
            </p>
            <p>
              <strong>Classic and Formal</strong> – Beyond the white dress and black tux, you’ll
              want a simple but elegant accent color to feature across your wedding—blue is a
              memorable and timeless choice that will pop on your invitations.
            </p>
            <p>
              <strong>Winter</strong> – Match your custom wedding invitations to the season. Your
              blue wedding invitations will welcome your guests to the winter wonderland you’ve
              designed for your nuptials.
            </p>
            <p>
              <strong>Beach</strong> – Complement nature’s color palette with blue wedding
              invitations. Your guests will look at your invitations and see cloudless skies and a
              crystal clear ocean.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the ideal blue invitations for our upcoming wedding?',
        body: (
          <>
            <p>
              Now that you’re imagining just how well blue wedding invitations might pair with your
              ideal wedding theme, you may be wondering how to inject the right amount of
              personality into your invitation designs.
            </p>
            <p>
              Zola can help you bring your tastes, interests, and wedding vision to life with style
              options such as:
            </p>
            <p>
              <strong>Foil</strong> – Whether you lean more toward gold or silver, many foil options
              are available for your wedding invitations. Add an eye-catching accent to contrast the
              simplicity of your blue palette—consider gold to match the hues of a beachfront
              sunset, or silver to evoke the simplicity of silver bells at a winter wedding.
            </p>
            <p>
              <strong>Custom Photo</strong> – Put yourself front and center on your wedding
              invitations with a photo. Whether you’ve taken engagement photos against a blue,
              beachy backdrop or snowy wonderland, guests will love seeing your smiling faces.
            </p>
            <p>
              <strong>Edges</strong> – Subtle details bring your design together. A soft, ethereal
              blue will mesh beautifully with rounded corners, while a vibrant tone will come to
              life against the sharp edges of square corners.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we use Zola to design our dream wedding invitations?',
        body: (
          <>
            <p>
              Once you’ve found the love of your life and set your wedding plans in motion, let Zola
              help you create the perfect blue wedding invitations. With free guest book addressing,
              free envelopes, and free there’s no better way to get the word out about your special
              day.
            </p>
            <p>
              Welcome your guests with custom wedding invitations from Zola—we’re more than just a
              resource, we’re your partner in planning the perfect wedding.
            </p>
          </>
        ),
      },
    ],
  },
  burgundy: {
    description:
      "Zola's burgundy wedding invitation designs are a deep, rich way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          Named after wine from the famous region of France, Burgundy is a deep, rich color sure to
          give your wedding invitation cards a luxurious touch. Burgundy adds dimension to any
          wedding invitation and captures the strength of love.
        </p>
        <p>
          You can lighten up your burgundy{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          with delicate metallic finishes, or enrich them with timeless letterpress printing. No
          matter the design, burgundy provides a powerful backdrop for the announcement of your
          wedding day.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with burgundy wedding invitations?',
        body: (
          <>
            <p>
              Burgundy is deep and dramatic. You can tailor burgundy wedding invitation designs to
              fit an assortment of wedding vibes:
            </p>
            <p>
              <strong>Romantic</strong> - There’s a reason we associate red with love. To highlight
              the romance of your celebration, consider burgundy wedding invitations for a more
              contemporary take on a classic.
            </p>
            <p>
              <strong>Formal</strong> - Red wine, red velvet, red lipstick—all of these luxuries
              share the richness and strength of burgundy. With more dimension than classic
              black-and-white color schemes, burgundy is an opulent choice for your wedding theme.
            </p>
            <p>
              <strong>Winery</strong> – Are you and your partner wine aficionados? Burgundy is the
              perfect color for a winery wedding—it’s inspired by the deep reds of Burgundy wines,
              some of the most renowned in the world. The rich hue will beautifully contrast the
              earthy greenery and airy blues of the landscape at your venue.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we create the ideal burgundy invitations for our wedding?',
        body: (
          <>
            <p>
              Burgundy is a beautiful color, but it’s not the only element you can use to add style
              to your wedding invitations.
            </p>
            <p>
              On Zola, dress up your wedding invitations with many stylistic choices, including:
            </p>
            <p>
              <strong>Foil</strong> – You can brighten burgundy’s richness with foil accents in gold
              or silver. This option adds a metallic finish to your invitations that will create the
              perfect balance of dark and light, sure to impress your guests.
            </p>
            <p>
              <strong>Letterpress</strong> – Have you fallen in love with burgundy’s depth?
              Letterpress font printing creates raised surfaces to add even more dimension to your
              burgundy wedding invitations.
            </p>
            <p>
              <strong>Style</strong> – Complement burgundy with a seasonal or destination invitation
              style. Whether you’re having a cozy winter wedding or a glamorous getaway to wine
              country, choose from many wedding invitation styles to match your wedding venue.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we use Zola to design the perfect wedding invitations?',
        body: (
          <>
            <p>
              You’ve already found a love worthy of burgundy’s beauty—let Zola help with the rest!
              Aside from offering many wedding invitation designs for you to choose from, Zola also
              offers perks like five free design samples, free free envelopes, free guest
              addressing, and free change the dates.
            </p>
            <p>With Zola by your side, the journey to your big day just got easier.</p>
          </>
        ),
      },
    ],
  },
  coral: {
    description:
      "Zola's coral wedding invitation designs are a fresh, invigorating way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          Feeling peachy keen about spending your life with your spouse-to-be? Choose a{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>{' '}
          that matches how you feel. Coral wedding invitations are warm and inviting, creating a
          glowing backdrop for both simple and intricate designs.
        </p>
        <p>
          As an accent color, coral pops against neutral or dark backgrounds. As part of vibrant
          designs like blooming florals, geometric borders, and dreamy illustrations, it contributes
          to a bold, bright, beautiful wedding invitation.
        </p>
        <p>
          No matter your design style, pops of coral are fun and exciting—the perfect way to usher
          in a day of good vibes.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with coral wedding invitations?',
        body: (
          <>
            <p>
              Coral elicits visuals of light-soaked sunsets, beautifully marrying pink and orange
              tones. It’s soft, yet also bright and uplifting.
            </p>
            <p>
              A coral wedding invitation is the perfect complement to one of these wedding vibes:
            </p>
            <p>
              <strong>Bohemian and Whimsical</strong> – Make your wedding day warm and inviting with
              deep, coral-colored florals or bright, geometric designs.{' '}
              <a
                href="/wedding-planning/invitations/shop/boho"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bohemian wedding invites
              </a>{' '}
              are the perfect sneak peek to a{' '}
              <a
                href="/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                venue
              </a>{' '}
              adorned in warm lighting, funky decor, and earthy details.
            </p>
            <p>
              <strong>Summer Beachy</strong> - With natural beauty at the center of a summertime
              beach wedding, make a sunny coral detail the focus of your wedding invitation suite.
              This warm hue was pretty much meant for a welcoming{' '}
              <a
                href="/wedding-planning/invitations/shop/beach"
                target="_blank"
                rel="noopener noreferrer"
              >
                beach wedding invitation
              </a>
              .
            </p>
            <p>
              <strong>Rustic</strong> – Wood accents pair effortlessly with coral’s warm undertones.
              Rather than opting for a pastel or earthy color scheme, brighten up your barn or
              backyard wedding with an upbeat and trendy{' '}
              <a
                href="/wedding-planning/invitations/shop/rustic"
                target="_blank"
                rel="noopener noreferrer"
              >
                rustic wedding invitation
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading:
          'How do we design the ultimate coral wedding invitations for our personal tastes?',
        body: (
          <>
            <p>
              Before you finalize your wedding day bouquets and attire, you must first create your
              coral wedding invitations. On Zola, personalize your invitations with a variety of
              customization options:
            </p>
            <p>
              <strong>Custom Photo</strong> – Embellish a warm invitation with your happy smiles.
              It’s a great way to connect with your guests even before the wedding.
            </p>
            <p>
              <strong>Edges</strong> – Whether you’re exchanging your vows on the beach or under an
              altar, consider customizing the shape of your wedding invitations to match the big
              day. Rounded edges go beautifully with the soft hues of whimsical weddings and sunset
              ceremonies, while square corners hint at modern styles and upbeat fun.
            </p>
            <p>
              <strong>Paper Type</strong> – With options like rugged, recycled paper and glimmering
              pearlescent sheen, you can customize your invitations with a paper type that matches
              your wedding vibe.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our dream wedding invites?',
        body: (
          <>
            <p>
              No matter your wedding color of choice, make your wedding invitations stand out with a
              little help from Zola’s designs, matching{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , and virtual personalization studio.
            </p>
            <p>
              We offer a vast selection of wedding invitation styles, designs, and color scheme
              options. If you can’t decide on a perfect match, pick your five favorites to receive
              as free samples before you place your order. Once you’ve landed on the perfect wedding
              invitation, enjoy free guest addressing, envelopes, and change the dates. It’s time to
              make your wedding shine with Zola.
            </p>
          </>
        ),
      },
    ],
  },
  dusty_blue: {
    description:
      "Zola's dusty blue wedding invitation designs are the perfect way to invite cherished ones to your celebration. Enjoy free addressing and envelopes!",
    summary: (
      <>
        <p>
          With hints of classic beauty, modern taste, and subtle elegance, dusty blue wedding
          invitations are a great choice to welcome your friends and family to your big day. The
          color conveys both warmth and refinement, telling all your guests precisely what your
          wedding day means to you.
        </p>
        <p>
          Dusty blue wedding invitation sets can be embellished with floral patterns and
          illustrations for a spring ceremony, or decorated with silver foil if you’re planning a
          wintry wedding.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with dusty blue wedding invitations?',
        body: (
          <>
            <p>
              Dreamy and romantic, dusty blue is a complex hue that pairs beautifully with a number
              of styles, including:
            </p>
            <p>
              <strong>Vintage</strong> – Go ahead and check off something old and something blue!
              With an updated take on this classic wedding theme, cross off something new, too.
              Dusty blue is the perfect complement to a{' '}
              <a
                href="/wedding-planning/invitations/shop/vintage_retro"
                target="_blank"
                rel="noopener noreferrer"
              >
                vintage wedding invitation
              </a>{' '}
              with its subtle faded hues, invoking a timeless elegance repurposed for today.
            </p>
            <p>
              <strong>Spring</strong> – Nothing says rebirth, renewal, and new beginnings quite like
              spring. With dusty blue{' '}
              <a
                href="/wedding-planning/invitations/shop/spring"
                target="_blank"
                rel="noopener noreferrer"
              >
                spring wedding invitations
              </a>
              , you can capture the feeling of spring in all its light and airy glory. Your dusty
              blue invitations will look right at home with your fresh spring bouquet, rain or
              shine.
            </p>
            <p>
              <strong>Bohemian & Romantic</strong> – Perfect for the urban weddings with dreamy
              decor, dusty blue is soft and sweet. We love it for modern and eccentric{' '}
              <a
                href="/wedding-planning/invitations/shop/boho"
                target="_blank"
                rel="noopener noreferrer"
              >
                boho wedding invitations
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect dusty blue wedding invitations for our big day?',
        body: (
          <>
            <p>
              Let Zola be your design partner when it comes to creating an exquisite wedding
              invitation. Once you have your color palette—the illustrious dusty blue—you can
              further personalize your invitations with design choices such as:
            </p>
            <p>
              <strong>Letterpress</strong> – Make an impression (pun intended!) with your wedding
              invitations. Our letterpress option allows you to create raised designs on your
              invitations to showcase your dusty blue color palette in contrasting textures.
            </p>
            <p>
              <strong>Paper</strong> – Take the vintage feel of your wedding to another level with
              natural paper, or embrace the soft delicacy of dusty blue with linen paper.
            </p>
            <p>
              <strong>Edges</strong> – To showcase the light, delicate qualities of your dusty blue
              color palette, consider adding subtle rounded corners to soften the look and feel of
              your invitations.
            </p>
          </>
        ),
      },
      {
        subheading: 'How does Zola help us find the perfect dusty blue wedding invitations?',
        body: (
          <>
            <p>
              A lot goes into wedding planning. Zola is here to help you customize the ideal dusty
              blue{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              and matching{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              for your wedding day. Still torn between themes, colors, and all the details that go
              along with designing an invitation? Take advantage of our free samples and choose five
              different designs to try out before you place your order.
            </p>
            <p>
              Every wedding is different! Zola’s committed to helping you design the wedding you’ve
              always wanted.
            </p>
          </>
        ),
      },
    ],
  },
  emerald_green: {
    description:
      "Zola's emerald green wedding invitations are a rich, inspired way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          For a wedding invitation that shines with elegance and class, emerald green is sure to
          please. While there’s something inherently royal about this gemstone shade, it fits nicely
          into rustic and outdoor weddings, too.
        </p>
        <p>
          Whether you’ve got your heart set on a regal wedding or tranquil celebration, find an
          emerald green{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>{' '}
          that suits your tastes on Zola.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with emerald green wedding invitations?',
        body: (
          <>
            <p>
              When it comes to a color palette, emerald green is like a chameleon. Because of its
              versatility, it pairs well with an array of venues, styles, seasons, flowers, and
              other colors.
            </p>
            <p>Emerald green could add vibrance to so many different weddings:</p>
            <p>
              <strong>Fall Forest</strong> – Set among an abundance of fragrant spruce trees, a
              rustic wedding is the perfect opportunity to decorate with emerald accents and autumn
              foliage.
            </p>
            <p>
              <strong>Bohemian and Botanical</strong> – Calling all boho flower lovers! Add to the
              essence of your garden or backyard wedding with leafy, emerald touches.
            </p>
            <p>
              <strong>Romantic and Formal</strong> – Hosting a celebration that’s romantic and
              timeless? For a majestic effect, pair green satin dresses or decor with
              emerald-accented invitations.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the emerald wedding invitations of our dreams?',
        body: (
          <>
            <p>
              You may have a clear idea about how to incorporate emerald green into your decor and
              attire, but where should you start with your wedding invitation design?
            </p>
            <p>
              On Zola, getting an eye for design has never been easier. Personalize your{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , invitations, and programs with a few of these stylistic details:
            </p>
            <p>
              <strong>Foil</strong> – For a small dose of luxury, add a foil with metallic shimmer.
              Gold and emerald green give off warm, enchanted forest vibes, while silver and emerald
              create an air of formal regality.
            </p>
            <p>
              <strong>Paper Type</strong> – If you love nature, opt for wedding stationery that’s
              good for the earth. Zola’s recycled and natural papers add a light, textured feeling,
              making your invitations green in more ways than one.
            </p>
            <p>
              <strong>Edges</strong> – Soft and rounded for a warm, rustic wedding, or sleek and
              straight for a sophisticated celebration? Experiment with how the shape of an
              invitation can echo the energy of your wedding.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design the perfect wedding invitations?',
        body: (
          <>
            <p>
              The countdown to your big day has begun and it’s time to spread the news far and wide.
              The wedding experts at Zola can help you with wording, invitation etiquette, and
              design. And, as always, we include free guest addressing, and envelopes to help give
              you more peace of mind.
            </p>
            <p>
              You won’t have to go digging for this jade-colored gemstone. Your perfect emerald
              wedding invitations are easy to find and customize on Zola.
            </p>
          </>
        ),
      },
    ],
  },
  gold: {
    description:
      "Zola's gold wedding invitation designs are a prestigious way to invite cherished ones to your celebration. Enjoy free envelopes and guest addressing!",
    summary: (
      <>
        <p>
          Gold is timeless and luxurious, perfect for any wedding theme. A gold wedding invitation
          possesses a joyful elegance that will charm any guest. What better color to celebrate your
          love?
        </p>
        <p>
          Whether you’re going for a geometric, Art Deco look à la Great Gatsby or looking to bring
          some sparkle to a bohemian wedding, a gold wedding invitation is sure to represent your
          wedding day with class and style.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with gold wedding invitations?',
        body: (
          <>
            <p>
              Gold is a go-to color for many happy couples, but that doesn’t mean your gold wedding
              invitations won’t stand out. There are so many ways you can use gold to suit your
              wedding vibe:
            </p>
            <p>
              <strong>Vintage</strong> – Gold foil wedding invitations possess the beauty of a
              coveted piece of vintage jewelry. For a vintage-inspired wedding, gold can bring to
              mind the precious feeling of a cherished heirloom—reflect this feeling with{' '}
              <a
                href="/wedding-planning/invitations/shop/vintage_retro"
                target="_blank"
                rel="noopener noreferrer"
              >
                vintage wedding invitations
              </a>
              !
            </p>
            <p>
              <strong>Beach</strong> – Do you love the sand and the sun? From wide-open skies to
              light reflecting off the waves, you’re bound to find gold on the beach. Gold wedding
              invitations are a vibrant and lovely choice for your beach wedding day.
            </p>
            <p>
              <strong>Classic</strong> – It’s called the “gold standard” for a reason. You can’t go
              wrong with gold for your{' '}
              <a
                href="/wedding-planning/invitations/shop/classic_traditional"
                target="_blank"
                rel="noopener noreferrer"
              >
                traditional wedding invitations
              </a>
              , adding a touch of opulent flair to your decor.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect gold wedding invitations for our special day?',
        body: (
          <>
            <p>
              It’s easy to add a little shine to any wedding invitation with gold accents. But there
              are many more ways you can get creative with your gold wedding invitations using
              Zola’s invitation design studio, such as:
            </p>
            <p>
              <strong>Custom Photo</strong> – Adding a custom photo of you and your partner to your
              wedding card is a great way to incorporate a personal touch. A gold wedding invitation
              is sure to make your smiles shine. Just be sure to coordinate your wardrobe with the
              gold accents, sticking to either neutral shades or a complementary palette like
              burgundy or royal blue.
            </p>
            <p>
              <strong>Letterpress</strong> – The letterpress printing technique creates raised
              surfaces that bring dimension and texture to your wedding invitations. Adding gold
              text with letterpress technique offers a delicate, timeless way to mark your special
              occasion.
            </p>
            <p>
              <strong>Design</strong> – Depending on how you use it in your design, gold can appear
              either sharp or soft. For a sharp look, go with geometric lines and patterns on your
              invitations. If you want a softer effect, try delicate script fonts or light brushes
              of gold foil.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we design our dream wedding invitations using Zola?',
        body: (
          <>
            <p>
              Once you’ve worked out details like your wedding date, venue, and wedding theme, it’s
              time to pick out the perfect{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>
              . When you work with Zola to design your invitations, you’re as good as gold. You can
              even receive five free design samples, along with other perks like free shipping,
              envelopes, and guest addressing.
            </p>
            <p>
              Zola makes designing your dream wedding invitations easy, so you can focus on what
              matters most: celebrating your love.
            </p>
          </>
        ),
      },
    ],
  },
  green: {
    description:
      "Zola's green wedding invitation designs are an earthy, modern way to invite cherished ones to your celebration. Enjoy free guest addressing and more!",
    summary: (
      <>
        <p>
          Earthy, vibrant, and botanical, green{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          are nothing short of luxurious. From deep sage to cool mint, the spectrum of shades is
          bursting with options sure to complement a number of wedding invitation styles.
        </p>
        <p>
          As a symbol of prosperity and luck, green not only captures several elements of the
          natural world, making it ideal for an outdoor wedding, but also bodes well for prosperous
          years ahead.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with green wedding invitations?',
        body: (
          <>
            <p>
              Whether you’re having an intimate backyard ceremony or a tropical destination wedding,
              green invitations and accents can be designed in a number of ways to fit your wedding
              vision.
            </p>
            <p>
              Marry these wedding styles with a green wedding invitation for a tastefully planned
              event your guests will rave about:
            </p>
            <p>
              <strong>Destination</strong> - If wedding wanderlust brought you to the jungle or the
              foot of a waterfall, this wedding color will make your{' '}
              <a
                href="/wedding-planning/invitations/shop/destination"
                target="_blank"
                rel="noopener noreferrer"
              >
                destination wedding invitations
              </a>{' '}
              pop.
            </p>
            <p>
              <strong>Beach</strong> – From seafoam to seaweed, green hues will complement your
              waterfront wedding and make the backdrop truly come alive.
            </p>
            <p>
              <strong>Spring Garden</strong> – Put Mother Earth and her seasonal blooms on full
              display with green accents and accessories that capture the essence of rebirth, just
              like your springtime celebration.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design our ideal green wedding invitations?',
        body: (
          <>
            <p>
              You’re easily captivated by the natural allure of green, but how can you incorporate
              it into your wedding invitation design? Zola makes it easy for you to customize your
              invitation to your liking.
            </p>
            <p>
              Consider these stylistic options to help make the most of your green wedding
              invitation:
            </p>
            <p>
              <strong>Style</strong> – Bring your invitations to life with twisting vines and a
              delicate flower. Or, for a personal touch, decorate with greenery akin to the
              varieties you’ll be adding to your bouquets—there’s nothing like an invitation that
              doubles as a keepsake.
            </p>
            <p>
              <strong>Custom Photo</strong> – Whether it’s your favorite shot from your dreamy
              engagement session or a classic candid, personalize your invitations with a photo set
              against a juniper or jade background. You might also consider a classic black and
              white portrait to let the greenery pop.
            </p>
            <p>
              <strong>Paper Type</strong> – Fully embrace your green wedding by opting for an
              invitation printed on recycled or natural wedding stationery. You can feel good about
              being kind to the environment while also sending a wedding invitation design you and
              your guests will love.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design green wedding invitations we’ll love?',
        body: (
          <>
            <p>
              Pick your palette and decide on the details for all your wedding paper
              needs—invitations,{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , programs, thank you cards, and more. If you fall in love with a few options, order
              up to five free samples to help you narrow them down. Receive free guest addressing,
              and envelopes for your top choice.
            </p>
            <p>
              Let love bloom with a green wedding invitation that’s planted, watered, and picked
              right here at Zola.
            </p>
          </>
        ),
      },
    ],
  },
  lavender: {
    description:
      "Zola's lavender wedding invitation designs are a beautiful, calming way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          Invoking beautiful florals, delicate fabrics, and total serenity, lavender wedding
          invitations are a fantastic choice for your wedding day. This light, airy hue is the
          perfect accent color, whether you’re dreaming of a sweet ceremony with a muted color
          scheme or pastels for a spring wedding.
        </p>
        <p>
          Considering a light purple watercolor{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>{' '}
          or a nature-inspired lavender{' '}
          <a
            href="/wedding-planning/invitations/shop/floral"
            target="_blank"
            rel="noopener noreferrer"
          >
            floral wedding invitation
          </a>
          ? There’s a world of possibilities out there with accents, textures, and designs to make
          your lavender wedding invitations pop.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with lavender wedding invitations?',
        body: (
          <>
            <p>
              Named for the plant that shares its color, lavender certainly has botanical
              connotations. But this doesn’t mean lavender wedding invitations are restricted to
              springtime blooming season or even outdoor weddings.
            </p>
            <p>The following wedding styles match perfectly with the soft tones of lavender:</p>
            <p>
              <strong>Vineyards and Wineries</strong> – Paired with your favorite glass of white,
              red, or sparkling champagne, a lavender palette can bring quiet elegance to your
              vineyard wedding and act as a great complement to the lush greens and pale blue skies.
            </p>
            <p>
              <strong>Romantic</strong> – Soft, delicate tones like lavender automatically hint at
              romance. For the intimate and idyllic ceremony, lavender wedding invitations will hit
              the mark.
            </p>
            <p>
              <strong>Whimsical</strong> – If you want to imbue your wedding day with a hint of
              whimsy and enchantment, consider the tones of lavender. Soft yet mysterious, lavender
              can feel almost magical in the right setting.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect lavender wedding invitations?',
        body: (
          <>
            <p>
              Lavender wedding invitations can make an understated and sweet statement about your
              big day, but there’s more to it than just picking a color scheme when it comes to
              crafting a phenomenal wedding invitation.
            </p>
            <p>On Zola, give a personal touch to your invitations with the following options:</p>
            <p>
              <strong>Style</strong> – Eager to customize your lavender palette with everything from
              calligraphy to botanical designs? Our style options open up a world of choices to
              create invitations that match your mood.
            </p>
            <p>
              <strong>Letterpress</strong> – Add a pop of texture to your otherwise simple, delicate
              lavender invitation with letterpress options. When your guests hold your invitations
              in their hands, they’ll be able to feel the thoughtful and tasteful details.
            </p>
            <p>
              <strong>Paper Type</strong> – Let the natural essence of lavender come through in a
              paper type that complements your palette. You can print your lavender wedding
              invitation on natural, recycled, or soft linen wedding stationery to embody your
              natural wedding inspiration.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our dream lavender wedding invitations?',
        body: (
          <>
            <p>
              Planning a wedding is all about the details. That’s why Zola has you covered when it
              comes to perfecting your lavender wedding invitations. With matching{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , free envelopes, guest addressing, and five free design samples, take all the time
              you need to create exquisite invitations.
            </p>
            <p>
              If you’re looking for an easy, flexible, and fun way to make your big day even more
              special, look no further than Zola.
            </p>
          </>
        ),
      },
    ],
  },
  navy: {
    description:
      "Zola's navy blue wedding invitation designs are a bold, loving way to invite cherished ones to your celebration. Enjoy free guest addressing and more!",
    summary: (
      <>
        <p>
          Navy blue wedding invitations could be the perfect option for you, whether you’ll be
          standing on a beach, a boat, or in a backyard. You can pair navy blue with pearlescent
          paper for a glossy sheen, much like the tips of a cresting wave, or add modern flair to a
          classic color scheme with rounded corners—the options are practically endless with a navy
          blue{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>
          .
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with navy blue wedding invitations?',
        body: (
          <>
            <p>
              Referring originally to the color of the Royal Navy’s iconic jacket, this deep color
              has always carried a sense of sophistication. Pair the rich tones of navy blue wedding
              invitations with one of the wedding themes below:
            </p>
            <p>
              <strong>Fall and Winter</strong> - Encourage your guests to admire the falling leaves
              or the flying snowflakes with navy blue wedding invitations. Whether the season brings
              a crisp autumn breeze or a downright chill, the deep hues of navy blue match the
              darker tones usually associated with the colder months, making this color ideal for{' '}
              <a
                href="/wedding-planning/invitations/shop/fall_autumn"
                target="_blank"
                rel="noopener noreferrer"
              >
                fall
              </a>{' '}
              or{' '}
              <a
                href="/wedding-planning/invitations/shop/winter"
                target="_blank"
                rel="noopener noreferrer"
              >
                winter wedding invitations
              </a>
              .
            </p>
            <p>
              <strong>Nautical</strong> – When dreaming of the high seas, no color comes to mind
              faster than navy blue. This is a great option whether your wedding day is an afternoon
              cruise or a waterfront affair.
            </p>
            <p>
              <strong>Formal and Romantic</strong> – If you’re looking for something elegant and
              classic without fully embracing the traditional black and white palette, consider pops
              of navy as a contrasting color to the rest of your wedding invitation. The deep tones
              embody romance while adding some visual interest to a{' '}
              <a
                href="/wedding-planning/invitations/shop/simple_minimalist"
                target="_blank"
                rel="noopener noreferrer"
              >
                simple wedding invitation
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the perfect navy blue wedding invitations?',
        body: (
          <>
            <p>
              When you choose Zola to help you design your invitations, you’re unlocking a world of
              possibilities, including these customization options:
            </p>
            <p>
              <strong>Edges</strong> – Give your invitations a shape that makes them stand out from
              the rest. Enhance your navy blue color palette with rounded corners for an air of
              dynamic elegance, much like ocean waves.
            </p>
            <p>
              <strong>Style</strong> – Looking to bring your guests on a journey with your
              invitations? Consider telling the story of your wedding day with style and design
              options like a lighthouse, anchor, or seashells, depending on your location and theme.
            </p>
            <p>
              <strong>Paper Type</strong> – Navy blue is most often a regal color, enhanced by a
              thick paper type that speaks to its royal roots. However, by customizing your wedding
              stationery type to something lighter like linen, recycled, or pearlescent paper, you
              can contrast the heavy hues and find true balance within your wedding invitation
              suite.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our dream wedding invitations?',
        body: (
          <>
            <p>
              Zola is committed to bringing you the best design options and customization
              possibilities for your wedding invitations. Order your choice of five free samples to
              see designs in person and find your dream wedding invitations.
            </p>
          </>
        ),
      },
    ],
  },
  pink: {
    description:
      "Zola's pink wedding invitation designs are a fun, charming way to invite cherished ones to your celebration. Enjoy free addressing and envelopes!",
    summary: (
      <>
        <p>
          Whether you prefer a bold berry pink or something soft and subdued, a pink wedding
          invitation evokes just as much joy as the news of your wedding day. It’s a stunning
          selection for a summer or spring wedding, and a perfect way to pair romance with a touch
          of vintage flair.
        </p>
        <p>
          With its youthful feel and formal flair, pink can dress up or energize any wedding day
          celebration—the difference is in the details.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with pink invitations?',
        body: (
          <>
            <p>
              Whether you choose a dusty pink wedding invitation or something bold and spectacular,
              it’s sure to be a charming complement to an array of venues and themes.
            </p>
            <p>
              From seaside soirée to colorful garden celebration, any wedding setting can be
              enhanced by pink:
            </p>
            <p>
              <strong>Floral & Romantic</strong> – Picture a petal-infused pathway lined with
              seating for your closest family and friends. If an intimate backyard is where you’ll
              be saying “I do,” you can’t go wrong with a pink wedding invitation.
            </p>
            <p>
              <strong>Summer Winery</strong> – If you’re making it official at a winery, pick{' '}
              <a
                href="/wedding-planning/invitations/shop/summer"
                target="_blank"
                rel="noopener noreferrer"
              >
                summer wedding invitations
              </a>{' '}
              that share the color of a glass ofrosé. With a pale blue sky and earthy greenery as
              your backdrop, pink tones will effortlessly match your intended vibe while providing
              their own pop of color.
            </p>
            <p>
              <strong>Beach</strong> – Tying the knot on white sand dunes? Pull in the pink hues of
              a soft sunset with a{' '}
              <a
                href="/wedding-planning/invitations/shop/beach"
                target="_blank"
                rel="noopener noreferrer"
              >
                beach wedding invitation
              </a>{' '}
              full of blush, mauve, or peach.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect pink invitations for our upcoming wedding?',
        body: (
          <>
            <p>
              Zola makes personalizing your pink wedding invitation design a walk in the park. If
              you’re stuck on your invitation, consider these editing options to get you started:
            </p>
            <p>
              <strong>Foil</strong> – Add texture and a hint of shine with metallic foil, creating
              an invitation card that is classic and romantic. Silver and pink are a timeless
              wedding invitation pairing, especially in a light blush tone.
            </p>
            <p>
              <strong>Paper Type</strong> – For a bit of added glamour, consider printing on
              pearlescent paper. This will add a subtle touch of shine to both sides of your wedding
              invitation.
            </p>
            <p>
              <strong>Style</strong> – Add illustrations of wildflowers to your paper design or use
              watercolor accents to mimic the sky at dusk—there are so many style choices, big and
              small, for your wedding invitations.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help me design my dream wedding invites?',
        body: (
          <>
            <p>
              Share the big news and pink hues of your wedding day with invitations made by Zola.
              Whether you prefer simple pastels or hot pink flower petals, you can easily
              personalize the design of your{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              and{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              to match the look and feel of your big day.
            </p>
            <p>
              Can’t seem to decide on your paper type, silhouette, or foil? No worries! We’ll send
              you five free samples of your choice so you can be sure to find your match. For
              invitations full of joy, choose Zola.
            </p>
          </>
        ),
      },
    ],
  },
  plum: {
    description:
      "Zola's plum wedding invitation designs are a warm, luxurious way to invite cherished ones to your celebration. Enjoy free addressing and envelopes!",
    summary: (
      <>
        <p>
          Purple hues have long adorned the crowns, capes, and scepters of royalty. But plum is for
          more than kings and queens. Nowadays, plum{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          are a magnificent way to add boldness and vibrancy to your special day.
        </p>
        <p>
          Plum patterns and designs work with various{' '}
          <a href="/expert-advice/wedding-themes" target="_blank" rel="noopener noreferrer">
            wedding day themes
          </a>
          . Whether you’re adorning your wedding invitation with a decorative flower or using
          intricate vintage patterns, you can’t go wrong incorporating plum. A plum wedding
          invitation makes a bold statement guests are sure to love.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with plum wedding invitations?',
        body: (
          <>
            <p>
              Plum wedding invitations can elevate a wedding to royal heights. Consider a purple hue
              for the following wedding vibes:
            </p>
            <p>
              <strong>Bohemian and Whimsical</strong> – Many boho couples opt for lavender dresses,
              lilac bouquets, and periwinkle table toppers. Plum wedding invitations are a great way
              to instill feelings of mythical enchantment, months before guests even see your canopy
              of string lights and greenery.
            </p>
            <p>
              <strong>Vintage</strong> – Plum is an unconventional but excellent choice for
              timeless,{' '}
              <a
                href="/wedding-planning/invitations/shop/vintage_retro"
                target="_blank"
                rel="noopener noreferrer"
              >
                vintage themed weddings
              </a>
              . Its dark hues pop against the muted tones of black, white, and sepia often found in
              classic decorative elements of bygone decades.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design plum wedding invitations for our upcoming celebration?',
        body: (
          <>
            <p>
              A plum-colored wedding invite is striking, but needs additional detail to really stop
              the show. So, what makes an invitation truly shine?
            </p>
            <p>On Zola, customize your wedding invitation with stylish options:</p>
            <p>
              <strong>Foil</strong> – What’s more regal than the luster of metallic foil? Embellish
              your plum-colored wedding invitations with gold or silver accents to create a look fit
              for a queen.
            </p>
            <p>
              <strong>Edges</strong> – If you’re going the non-traditional or whimsical route,
              rounded invitation edges will add delicate softness to the deep purple hue. For a more
              formal affair, square edges create crisp, clean lines you’ll love.
            </p>
            <p>
              <strong>Style</strong> – Invitations can be customized to reflect your personal style.
              For example, layer delicate plum florals on top of a simple backdrop for understated
              elegance, or combine a bold plum base color with detailed adornments to up the wow
              factor.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our dream plum wedding invitations?',
        body: (
          <>
            <p>
              Once you’ve lined up your wedding date, found your venue, and chosen your theme, don’t
              forget to customize your wedding invitations on Zola. We have hundreds of matching{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the date
              </a>{' '}
              and invitation designs to choose from, with personalization options for each. If
              you’re having trouble deciding, go ahead and request samples of your five favorite
              designs, free of charge, before you place your final order.
            </p>
            <p>
              Switching up your paper type, letterpress, and custom photo is sure to make your
              wedding invitations truly special. For a bold statement, design with Zola.
            </p>
          </>
        ),
      },
    ],
  },
  purple: {
    description:
      "Zola's purple wedding invitation designs are a lush, luxurious way to invite cherished ones to your celebration. Enjoy free addressing and envelopes!",
    summary: (
      <>
        <p>
          Throughout history, purple has symbolized wealth, royalty, and opulence. With so many
          couples choosing light, airy wedding themes, purple wedding invitations can help you stand
          out from the crowd while conveying romance and style.
        </p>
        <p>
          Use deep purple in your{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          for a decadent, mysterious look, or add some light and fun with accents of silver or gold.
          Purple is a bold color, perfect for bold or eccentric couples.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with purple invitations?',
        body: (
          <>
            <p>
              Purple adds an exciting element to the standard wedding colors of black and white. If
              you’d like to bring this unique and colorful twist to your wedding day, here are some
              wedding themes that work well with a purple wedding invitation:
            </p>
            <p>
              <strong>Romantic and Formal</strong> – The deep, complex tones of purple convey the
              love you feel for your partner and bring that emotion to the forefront. Purple wedding
              invitations add lush, lovey-dovey vibes to your wedding theme while remaining regal
              and sophisticated.
            </p>
            <p>
              <strong>Fall</strong> – From falling leaves to pumpkin decor, purple is a gorgeous
              complement to the rich color combination of autumn. If you want to capture the beauty
              of your fall wedding date, incorporating purple into your{' '}
              <a
                href="/wedding-planning/invitations/shop/fall_autumn"
                target="_blank"
                rel="noopener noreferrer"
              >
                fall wedding invitations
              </a>{' '}
              will beautifully contrast to the browns, reds, and oranges of the season.
            </p>
            <p>
              <strong>Wineries</strong> – Purple’s depth is reminiscent of a full-bodied red wine,
              making it ideal for a{' '}
              <a href="/wedding-planning/invitations/shop/winery">winery wedding invitation</a>.
              Purple wedding invitations are sure to get your guests excited for your romantic
              vineyard wedding and will also complement the lighter colors of your outdoor setting.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can I design beautiful purple invitations for my wedding?',
        body: (
          <>
            <p>
              Purple is a strong color, but it shouldn’t overpower your wedding invitation. Zola’s
              wedding invitation design studio makes it easy to personalize your invitations with
              many options, including:
            </p>
            <p>
              <strong>Foil</strong> – Adding metallic foil accents is a great way to brighten up
              your purple wedding invitations. Gold foil will beautifully balance light and dark on
              your wedding invitations, while also highlighting that royal charm.
            </p>
            <p>
              <strong>Paper Type</strong> – The paper quality of your wedding invitation can help
              take it to the next level. A textured or pearlescent paper can add even more dimension
              to deep purple invitations.
            </p>
            <p>
              <strong>Design</strong> – Purple comes in many shades, from the deep reds of fine wine
              to the hazy hues of dusk. Choosing the shade of purple that’s right for you is key to
              reflecting the style and vibe of your wedding.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we design our dream purple wedding invitations using Zola?',
        body: (
          <>
            <p>
              No matter the venue or theme you’ve chosen for your wedding, Zola is here to help you
              create the wedding invitations of your dreams. To make the process even simpler, Zola
              offers perks like five free design samples. You’ll also save with free envelopes, free
              guest addressing, free and free change the dates—what’s not to love?
            </p>
          </>
        ),
      },
    ],
  },
  red: {
    description:
      "Zola's red wedding invitation designs are a beautiful, bold way to invite cherished ones to your celebration. Enjoy free envelopes, and more!",
    summary: (
      <>
        <p>
          For a{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>{' '}
          as bold as you, choose red. This color is every bit as sophisticated and romantic as it is
          striking and commanding. Because of its showstopping presence, you’ll love how it looks
          paired with warm wood accents, neutral shades, and a limited color palette. A color like
          red demands to take center stage on your big day.
        </p>
        <p>
          In the mood for classic romance? Let a red wedding invitation tell the story of your love.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with red wedding invitations?',
        body: (
          <>
            <p>
              Whether you’ve built a vision board lined with vintage flair or have carefully
              coordinated the details for a seasonal celebration, you’ll fall in love with the
              versatility of red wedding invitations.
            </p>
            <p>These wedding themes are a perfect match for the bold hue:</p>
            <p>
              <strong>Fall Rustin</strong> – Picture a ceremony in an apple orchard, filled with red
              fruit as crisp as the fall air. Or perhaps you want a mountainside gathering held just
              as the fiery foliage reaches its peak. The warm red hues of fall{' '}
              <a
                href="/wedding-planning/invitations/shop/rustic"
                target="_blank"
                rel="noopener noreferrer"
              >
                rustic invitations
              </a>{' '}
              will pair well with mulled cider, a light autumn breeze, and the earthy tones of the
              season.
            </p>
            <p>
              <strong>Formal and Sophisticated</strong> – Scarlet{' '}
              <a
                href="/wedding-planning/invitations/shop/elegant_formal"
                target="_blank"
                rel="noopener noreferrer"
              >
                formal invitations
              </a>{' '}
              are likely to turn heads, whether for a formal evening affair with crimson accents, or
              an upscale event that commands red-carpet attention.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect red invitations for our upcoming wedding?',
        body: (
          <>
            <p>
              Create an elegant red wedding invitation and tie it all together with these finishing
              touches:
            </p>
            <p>
              <strong>Foil</strong> – Looking to add a bit of luster to your invites? Gold foil will
              give your red-themed wedding invitations the perfect amount of upscale pizzazz, while
              metallic silver accents will perfectly suit a formal, black-tie ball.
            </p>
            <p>
              <strong>Edges</strong> – To enhance a color as bright and daring as red, use square
              edges to achieve a clean, classic silhouette.
            </p>
            <p>
              <strong>Paper Type</strong> – Why not match your paper style to the vibe of your
              party? You can choose light materials like natural, recycled, or linen paper to suit a
              casual, seasonal celebration, or smooth, pearlescent, or extra thick wedding paper to
              suit a more formal affair.
            </p>
          </>
        ),
      },
      {
        subheading: 'Why should we use Zola to design our dream invitations?',
        body: (
          <>
            <p>
              Once you’ve settled on a location and a date, let Zola help you share the exciting
              news with your guest list. Customize your{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , enclosure cards, wedding invitations, and more with a pop of passionate red.
            </p>
            <p>
              Plus, order five free design samples before settling on the perfect invitation suite.
              To turn your vibrant wedding vision into real-life romance, start with Zola.
            </p>
          </>
        ),
      },
    ],
  },
  rose_gold: {
    description:
      "Zola's rose gold wedding invitation designs are a luxe, modern way to invite cherished ones to your celebration. Enjoy free envelopes and addressing!",
    summary: (
      <>
        <p>
          When designing a wedding invitation with a metallic finish, gold and silver aren’t your
          only options. Rose gold—gold infused with a soft, pinkish hue—is delicate and sleek, a
          romantic way to declare your big day.
        </p>
        <p>
          It’s easy to add a shine to your celebration with a rose gold foil{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitation
          </a>
          , sure to catch the eye of your guests. You can also add accents of rose gold to other
          fonts or design elements, enhancing your rose gold wedding invitations.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with rose gold wedding invitations?',
        body: (
          <>
            <p>
              Rose gold is both classic and unconventional. Like the fine jewelry that inspires this
              color scheme, rose gold wedding invitations are elegant and set your wedding day
              apart. Here are a few wedding themes that work well with rose gold:
            </p>
            <p>
              <strong>Bohemian</strong> – If you and your partner are creative and adventurous, you
              may want your wedding to have a bohemian feel.{' '}
              <a
                href="/wedding-planning/invitations/shop/rose-gold"
                target="_blank"
                rel="noopener noreferrer"
              >
                Rose gold wedding invitations
              </a>{' '}
              are casual yet elegant, a great way to reflect your personalities.
            </p>
            <p>
              <strong>Whimsical</strong> – Weddings don’t always have to be formal and serious—give
              your special day a playful and joyous feel with rose gold wedding invitations. This
              color creates the perfect balance between sophistication and fun.
            </p>
            <p>
              <strong>Romantic</strong> – What’s a more classic symbol of romance than roses? With
              its signature blush of pink, rose gold is a subdued yet lovely choice for your{' '}
              <a
                href="/wedding-planning/invitations/shop/romantic"
                target="_blank"
                rel="noopener noreferrer"
              >
                romantic wedding invitation
              </a>
              .
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we create the perfect rose gold invitations for our wedding?',
        body: (
          <>
            <p>
              Incorporating rose gold is a great way to make your{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              and wedding invitations stand out. When customizing wedding invitations on Zola,
              experiment with many design elements:
            </p>
            <p>
              <strong>Foil</strong> – Rose gold wedding invitations have an even more elegant sheen
              when you incorporate reflective foil. It’s more unconventional than gold and silver,
              but feels just as classic. You can also add rose gold foil to neutral, monochromatic
              designs for a subtle pop of sparkle.
            </p>
            <p>
              <strong>Letterpress</strong> – Letterpress is a printing technique that gives your
              invitation card an artful and classy feel. Using the letterpress technique with rose
              gold will ensure your wedding invitations shine from every angle.
            </p>
            <p>
              <strong>Edges</strong> – You may want your rose gold invitations to look as soft as
              flower petals, an effect you can create with rounded corners. Alternatively, you can
              give your event a sleek and stylish feel with geometric wedding invitations that marry
              square corners with shimmering rose gold lines.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us create the perfect wedding invitations?',
        body: (
          <>
            <p>
              Zola makes the invitation design process easy. Bonus: you can receive up to five free
              design samples, as well as free guest addressing, envelopes, and change the dates.
            </p>
            <p>
              You deserve to shine light on your love. From designing your wedding invitations to
              gearing up for your big day, Zola is here to help you sparkle.
            </p>
          </>
        ),
      },
    ],
  },
  royal_blue: {
    description:
      "Zola's royal blue wedding invitation designs are a classic, timeless way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          From kings and queens to dukes and duchesses, regality and sophistication surround the
          royal blue color scheme. If you’re planning a royal-inspired wedding, the perfect place to
          start is with royal blue{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>
          .
        </p>
        <p>
          But this vivid color isn’t just restricted to monarchs. Placed in a rustic setting, it
          serves as a bright pop of color. Set against the backdrop of the ocean and sky, the darker
          hue contrasts a sea of beautiful blue. In a downtown venue with vintage flair, it’s a
          sophisticated yet vibrant color choice. No need for crowns and tiaras when it comes to a
          royal blue wedding invitation, but we’ll never say no to dressing up.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with royal blue wedding invitations?',
        body: (
          <>
            <p>
              Royal blue is an elegant choice for a variety of wedding themes with rich and inviting
              tones. When it comes to decor and accent colors, you’ll find it pairs well with
              wedding styles such as:
            </p>
            <p>
              <strong>Rustic</strong> – Whether you’re bringing a little country to the city or
              heading out to the farm for an intimate ceremony, royal blue is a great complement to
              the browns, oranges, and reds of a{' '}
              <a
                href="/wedding-planning/invitations/shop/rustic"
                target="_blank"
                rel="noopener noreferrer"
              >
                rustic wedding invitation
              </a>
              . While it’s not the most traditional choice, you may be surprised by the stunning
              color combinations.
            </p>
            <p>
              <strong>Beach Destination</strong> – What could be more majestic than a destination
              wedding? Show your guests that they’ll be receiving the royal treatment on your
              special day using a royal blue palette for your{' '}
              <a
                href="/wedding-planning/invitations/shop/destination"
                target="_blank"
                rel="noopener noreferrer"
              >
                destination wedding invitation
              </a>
              .
            </p>
            <p>
              <strong>Formal</strong> – If you have an eye for regal festivities, look no further
              than royal blue. This perfect medium blue hue is both deep enough to hint at upscale
              sophistication, and light enough to pop against the darker tones of a black-tie
              affair.
            </p>
          </>
        ),
      },
      {
        subheading:
          'How can we create the perfect royal blue wedding invitations for our special day?',
        body: (
          <>
            <p>On Zola, create brilliant, personalized invitations using these style options:</p>
            <p>
              <strong>Foil</strong> – If you’re trying to put the royal in royal blue, why not
              double down on some tasteful foil accents in gold or silver?
            </p>
            <p>
              <strong>Paper Type</strong> – Explore a smooth and refined paper for sophisticated
              invitations, or a textured paper type for earthy balance to your royal blue.
            </p>
            <p>
              <strong>Custom Photo</strong> – Perhaps you want to include the true royal couple on
              your invitations. Now you can! Simply add a custom photo to your invitation.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design our ideal wedding invitations?',
        body: (
          <>
            <p>
              Even if no one calls you royalty (yet!), you’ll get the royal treatment when you
              design your wedding invitations on Zola. With five free design samples, free shipping,
              and free change-the-dates, Zola is here to help. If you’re ready to start planning,
              we’re ready to show you the perfect royal blue wedding invitations.
            </p>
          </>
        ),
      },
    ],
  },
  silver: {
    description:
      "Zola's silver wedding invitation designs are a sleek, modern way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          When modern elegance and timeless tradition intertwine, the result is a silver wedding
          invitation that’s sure to impress. If you’re looking to add just a touch of subtle glamour
          or eye-catching sparkle to your wedding theme, incorporate silver into your{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>{' '}
          in a few striking ways.
        </p>
        <p>
          For a sleek, monochromatic{' '}
          <a href="/wedding-planning/save-the-date/shop" target="_blank" rel="noopener noreferrer">
            save the date
          </a>{' '}
          and invitation set, pair silver with a palette of white, gray, and black. Or you might add
          a pop of coastal color, such as teal, on your silver invitations to remind guests of
          shimmering ocean waves. No matter which wedding invitation design you choose, your love is
          sure to shine.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with silver wedding invitations?',
        body: (
          <>
            <p>
              Here’s a better question: is there such a thing as a wedding day that wouldn’t shine
              with silver accents?
            </p>
            <p>This versatile choice can find a home in many weddings:</p>
            <p>
              <strong>Formal and Chic</strong> – The sleek look of silver is an ideal choice for
              industrial venues and formal,{' '}
              <a
                href="/wedding-planning/invitations/shop/elegant_formal"
                target="_blank"
                rel="noopener noreferrer"
              >
                black-tie invitations
              </a>
              .
            </p>
            <p>
              <strong>Winter</strong> – If you’ve opted to celebrate in a classic winter wonderland,
              silver{' '}
              <a
                href="/wedding-planning/invitations/shop/winter"
                target="_blank"
                rel="noopener noreferrer"
              >
                winter wedding invitations
              </a>{' '}
              will evoke the magic of fresh snow and glistening icicles.
            </p>
            <p>
              <strong>Beachy Bohemian</strong> – Hosting a sunset wedding on your favorite secluded
              beach? Silver-accented invitations will beautifully mimic the sparkle of sand and
              glistening sunlight dancing across cresting waves.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the perfect silver wedding invitations?',
        body: (
          <>
            <p>
              Adaptable and alluring, silver adds a dash of luxury to just about any wedding invite.
              When personalizing your paper designs on Zola, consider a few of these stylistic
              options:
            </p>
            <p>
              <strong>Letterpress</strong> – Line your invitations with metallic letterpress font
              for elegance and texture perfect for formal affairs.
            </p>
            <p>
              <strong>Paper Type</strong> – The right paper type can make all the difference,
              whether it’s a light linen paper that allows your metallic accents to really pop, or a
              pearlescent sheen to match the lustrous silver. With a subtle shimmer on both sides,
              pearlescent wedding stationery is a flawless choice for glamour and glow.
            </p>
            <p>
              <strong>Style</strong> – Since silver speaks volumes all on its own, consider opting
              for a minimalist design with plenty of white space, slim borders, and carefully placed
              accents.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design the wedding invitations of our dreams?',
        body: (
          <>
            <p>
              For a wedding day that’s polished, sophisticated, and effortlessly enchanting, start
              with silver wedding invitation cards from Zola. Personalize your invitations by adding
              a custom photo, textured letterpress, and more. Plus, take advantage of free shipping,
              envelopes, and wedding guest addressing, plus five complimentary samples that help you
              narrow down your many options.
            </p>
            <p>
              To let your love shine, start building your silver wedding invitations on Zola today.
            </p>
          </>
        ),
      },
    ],
  },
  teal: {
    description:
      "Zola's teal wedding invitation designs are a deep, calming way to invite cherished ones to your celebration. Shop now with free envelopes and more!",
    summary: (
      <>
        <p>
          Finally, the perfect “something blue” for your upcoming wedding: stunning teal
          invitations. Teal’s a bright yet peaceful color and allows you to customize your wedding
          invitation with a variety of options, from funky geometric designs to gold foil
          embellishments.
        </p>
        <p>
          With teal as your wedding invitation color, your big day’s sure to stand out—whether
          you’re planning a destination beach wedding, a rustic celebration, or an elegant urban
          soirée.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with teal wedding invitations?',
        body: (
          <>
            <p>
              Teal is a strong, bold color with a calming essence. It can be used to elevate your
              text, background, or design elements—including florals, feathers, and foliage. This
              versatility means it can easily complement a number of{' '}
              <a href="/expert-advice/wedding-themes" target="_blank" rel="noopener noreferrer">
                wedding themes
              </a>
              , such as:
            </p>
            <p>
              <strong>Whimsical and Bohemian</strong> – Planning a lantern-filled rustic wedding
              under a canopy of oak trees? Teal’s vibrant hues make a brighter statement than some
              other traditional options, but can add eccentric flavor to{' '}
              <a
                href="/wedding-planning/invitations/shop/rustic"
                target="_blank"
                rel="noopener noreferrer"
              >
                rustic wedding invitations
              </a>
              .
            </p>
            <p>
              <strong>Classic and Romantic</strong> – Teal is beautiful in all shades, but the most
              enchanting of all may be in pastel form. Add subtle beauty to your{' '}
              <a
                href="/wedding-planning/invitations/shop/classic_traditional"
                target="_blank"
                rel="noopener noreferrer"
              >
                classic wedding invitations
              </a>{' '}
              with a splash of soft teal watercolor. For a completely different yet equally romantic
              approach, consider dark teal wedding invitations on the opposite end of the color
              spectrum—the deep teal complements classic reds and dark blues for a striking palette.
            </p>
            <p>
              <strong>Beachy</strong> – If you’re getting married on the clear waters of a tropical
              destination, you’ll want an invitation to match. A teal{' '}
              <a
                href="/wedding-planning/invitations/shop/beach"
                target="_blank"
                rel="noopener noreferrer"
              >
                beach wedding invitation
              </a>{' '}
              perfectly conveys the environment of your wedding: aquamarine waters, azure skies,
              maybe some teal attire—it’s up to you!
            </p>
          </>
        ),
      },
      {
        subheading: 'How can we design the ideal wedding invitations for our celebration?',
        body: (
          <>
            <p>
              After choosing teal as your wedding invitation backdrop or accent color, consider
              customizing your invitations further with details to make the color pop.
            </p>
            <p>
              Using one of Zola’s teal designs, personalize your invitation card with a variety of
              creative customization options:
            </p>
            <p>
              <strong>Letterpress</strong> – Embellish your teal wedding invitations with raised
              text for a timeless and classic touch.
            </p>
            <p>
              <strong>Foil</strong> – There aren’t many color combinations quite as stunning as a
              deep, vibrant teal and gold foil. Foil stamping can add more dimension and a touch of
              glamour to any invitation design.
            </p>
            <p>
              <strong>Custom Photo</strong> – A personal photo adds sentimentality to any
              invitation, and could pair particularly well with a teal backdrop. For a portrait that
              matches your palette, consider a waterfront or winter photo with glimmers of ocean
              waves or snowy hills.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design the wedding invitations of our dreams?',
        body: (
          <>
            <p>
              With a zeal for teal and a little help from Zola, you can craft the perfect teal{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              and wedding invitations for your special day. Choose from hundreds of customizable
              designs, with the option to receive five free design samples in the mail before you
              place your invitation order.
            </p>
            <p>
              Using Zola, your <i>something blue</i> is taken care of. Check!
            </p>
          </>
        ),
      },
    ],
  },
  white: {
    description:
      "Zola's white wedding invitation designs are the perfect, in-style way to invite cherished ones to your celebration. Enjoy free guest addressing!",
    summary: (
      <>
        <p>
          There’s no color quite as classic and traditional as wedding white, from bridal gowns and
          three-tiered cakes to tablecloths, chair covers, and sweeping decor. This color palette
          adds an air of elegance and sophistication to any celebration.
        </p>
        <p>
          White also poses as the perfect backdrop for{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            personalized wedding invitation designs
          </a>
          —both simple and elaborate—whether you’re adorning the invites with elegant black
          lettering or decorating them with a colorful pattern.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Which kinds of weddings work best with white wedding invitations?',
        body: (
          <>
            <p>
              Ethereal and dreamy, pearl white wedding invitations are a match made in heaven for
              more than just traditional weddings.
            </p>
            <p>
              Here are a few wedding vibes that highlight the color’s vintage charm, simple
              elegance, or romantic splendor:
            </p>
            <p>
              <strong>Vintage</strong> – With lace, pearl embellishments, and ivory chandeliers at
              the helm, it’s no wonder retro weddings are perfectly suited to white{' '}
              <a
                href="/wedding-planning/invitations/shop/classic_traditional"
                target="_blank"
                rel="noopener noreferrer"
              >
                vintage wedding invitations
              </a>
              .
            </p>
            <p>
              <strong>Classic and Formal</strong> – If you’re married to the classics—black and
              white—then why not keep it uniform throughout your wedding paper? Consider simple,{' '}
              <a
                href="/wedding-planning/invitations/shop/classic_traditional"
                target="_blank"
                rel="noopener noreferrer"
              >
                traditional wedding invitations
              </a>{' '}
              with elegant typeface and sleek design.
            </p>
            <p>
              <strong>Romantic</strong> – A touch of immaculate white in your{' '}
              <a
                href="/wedding-planning/invitations/shop/boho"
                target="_blank"
                rel="noopener noreferrer"
              >
                romantic wedding invitations
              </a>{' '}
              can go a long way toward creating an atmosphere. Bring on the bouquets of pink roses,
              twinkling lights, and fanciful string quartet!
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the perfect white wedding invitations for our big day?',
        body: (
          <>
            <p>
              White may be elegant and sophisticated, but it’s also a naturally blank canvas.
              Whether you’re keeping it simple or matching your wedding’s extravagant style, a white
              wedding invitation grants you unlimited customization options.
            </p>
            <p>
              Here are a few special touches that work beautifully with any white wedding invitation
              card:
            </p>
            <p>
              <strong>Letterpress</strong> – Elevate your white custom invitations with raised
              letterpress font, perfectly complementing upscale, vintage, elegant, romantic, and
              formal celebrations.
            </p>
            <p>
              <strong>Style</strong> – The best thing about white is that it goes with
              everything—colorful patterns, minimalist sketches, vibrant illustrations, and more.
              White can either be the main event or supporting character of a truly stand-out
              invitation design.
            </p>
            <p>
              <strong>Paper Type</strong> – From formal, to rustic, to romantic, white is as
              versatile as it gets. Even a simple choice like your paper type can transform the
              feeling of your wedding invitation. Consider pearlescent paper for a subtle shimmer,
              linen for a light, airy vibe, or eggshell for formal flair.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can Zola help us design the white wedding invitations of our dreams?',
        body: (
          <>
            <p>
              You can’t go wrong with white{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              and wedding invitations—shades of white are a classic in any wedding color palette,
              after all. To enhance the versatility of this go-to color, Zola provides plenty of
              design and customization options to bring our wedding invitation designs to life. If
              you can’t decide between your favorite designs, order five free samples to ensure your
              invitations will be as flawless as crisp white paper.
            </p>
            <p>
              With your order, you’ll also receive free guest addressing, envelopes, and change the
              dates—just in case! For invitations and wedding inspiration, Zola’s talented designers
              have you covered.
            </p>
          </>
        ),
      },
    ],
  },
  disney: {
    description:
      "Zola's Disney wedding invitation designs are the perfect, in-style way to invite cherished ones to your celebration. Enjoy free guest addressing!",
  },
};
